/**
 * IndustryPipelineOverview — the single card that IS the redesigned Use Cases
 * page (Figma redesign, DAT-xxxx). Replaces PipelineMap as the top-level
 * component: same canvas, same helpers, different chrome + a bubble detail
 * panel that lives BELOW the chart instead of separate drill/back nav.
 *
 * Layout (top-down):
 *   1. Card header — title + 4-pill segmented selector
 *      [Industry View | Use Case View | Asset View | Account View].
 *   2. One or more `ChartCard`s stacked vertically. Each card owns its own
 *      filter row, snapshotDate, drill fetches, canvas, and (when it's the
 *      only card) the detail panel below its chart.
 *   3. In single-card mode: right of the filter row shows "+ Add New Chart".
 *      In multi-card mode: each card gets a "- Delete chart" link on the
 *      same row, and clicking any bubble pops a modal telling the user to
 *      close a card first (Figma nodes 2788:29254 / 2842:33427 / 2788:29820
 *      — the last one shows the Comparison Summary block which we deliberately
 *      skip until a backend op is defined).
 *
 * View mode is card-shared: switching Industry/UC/Asset/Account applies to
 * EVERY chart at once. Filters + snapshotDate are per-chart. This matches
 * the Figma "compare same view with different filters/snapshots" intent.
 *
 * Data sources per view mode (each chart fetches independently):
 *   - Industry View: GetPipelineBubblesCommand({groupBy: 'industry'}) —
 *     backend aggregates opps DISTINCT per (industry, opp_id) so cross-UC
 *     fanout doesn't double-count. Rows map straight to IndustryBubble.
 *   - Use Case View: GetPipelineBubblesCommand({industry}) → ucBubblesFromLive.
 *   - Account View: GetPipelineBubblesCommand({groupBy: 'account', industry});
 *     AccountInsights is derived client-side via deriveAccountInsights().
 *   - Asset View: parallel asset-grain + UC-grain fetches for NA-UC ghosts.
 *
 * Deliberately NOT wired here (each has a TODO in-place):
 *   - Y/X axis dropdown labels are visual only.
 *   - Calculator icon, Chart/Table toggle are placeholders.
 *   - Comparison Summary block (Figma 2788:29820) is NOT rendered until a
 *     backend op is defined that can compare two chart configs.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  Spinner,
  type PropertyFilterProps,
  type SelectProps,
} from '@amzn/awsui-components-react';
import {
  getIvtClient,
  GetPipelineBubblesCommand,
  ListAssetsCommand,
  AssetOrigin,
  type PipelineBubble,
  type AssetSummary,
} from '../../../client/IvtHubClient';
import { deriveAccountInsights, type AccountInsights } from '../../../utils/accountInsights';
import { ownIndustryOnlyQuery } from '../../../components/pipelineFilterShared';
import {
  hitBubbles,
  ucBubblesFromLive,
  assetBubblesFromPipeline,
  accountBubbles,
  X_AXIS_LABELS,
  bubbleLabel,
  type Bubble,
  type IndustryBubble,
  type UcBubble,
  type AssetBubble,
  type AccountBubble,
  type GhostBubble,
  type BubbleLayout,
  type AxisCfg,
} from './PipelineMap';
import IndustryDetailPanel from './IndustryDetailPanel';
import UseCaseDetailPanel from './UseCaseDetailPanel';
import AssetDetailPanel from './AssetDetailPanel';
import AccountDetailPanel from './AccountDetailPanel';
import GhostDetailPanel from './GhostDetailPanel';
import BubblesTable from './BubblesTable';
import AccountInsightsBar from './AccountInsightsBar';
import IpoFilters from './IpoFilters';
import IpoPipelinePropertyFilter from './IpoPipelinePropertyFilter';
import { useAuth } from '../../../../context/AuthContext';
import { fetchAllowedIndustries } from '../../../client/permissionClient';
import { queryToImpactFilterParam } from '../../../utils/impactPipelineFilter';
import {
  baselineChipText,
  baselinePoint,
  type BaselinePoint,
} from '../../../utils/impactBaseline';

// Figma 20260605 redesign: 每行业一个柔和粉彩色 (取自 Figma 气泡 SVG fill).
// 后端 industry code 可能是 LS_HC/ME_AD/GAMES/RTL_CPG_H 等 alias, 统一映射到同色.
const INDUSTRY_COLORS_FIGMA: Record<string, string> = {
  AUTO: '#FF86DF',
  MFG: '#FFBF75',
  HCLS: '#91DEFF',
  LS_HC: '#91DEFF',
  GAME: '#FFEE06',
  GAMES: '#FFEE06',
  MEAD: '#52EEC7',
  ME_AD: '#52EEC7',
  FSI: '#A2FF00',
  RCH: '#E6ACFF',
  RTL_CPG_H: '#E6ACFF',
  ECOM: '#FFBF75',
};

const FIGMA_LABEL_COLOR = '#424650'; // grey/650
const FIGMA_AXIS_LABEL_COLOR = '#5F6B7A';
const FIGMA_AXIS_LINE_COLOR = '#DEDEE3'; // grey/300

function industryColorFigma(code: string): string {
  return INDUSTRY_COLORS_FIGMA[code] ?? '#B7CEDB';
}

// Use Case View — priority swaps roles from the legacy scheme (Strategic used
// to be amber; Figma re-anchors it to a soft blue and moves the warm accent
// to Recommended). Ring hex + New-Asset dot hex are read from the exported
// Figma SVGs; both palettes are inside each priority entry so the drawing code
// stays one lookup per bubble.
interface PriorityFigmaStyle {
  ringColor: string;
  ringInnerBoost: string; // slightly lighter tint for the innermost disc
  ringAlphas: [number, number, number, number]; // outer → inner
  dotColor: string; // New-Asset satellite dot
}
const PRIORITY_FIGMA: Record<string, PriorityFigmaStyle> = {
  strategic: {
    ringColor: '#FFCB7D',
    ringInnerBoost: '#FFCF88',
    ringAlphas: [0.2, 0.4, 0.4, 0.8],
    dotColor: '#FF884C',
  },
  recommended: {
    ringColor: '#55C1FF',
    ringInnerBoost: '#68C8FF',
    ringAlphas: [0.2, 0.4, 0.4, 0.8],
    dotColor: '#2B87FF',
  },
  opportunistic: {
    ringColor: '#C8C8C8',
    ringInnerBoost: '#B0B0B0',
    ringAlphas: [0.2, 0.4, 0.4, 0.8],
    dotColor: '#7A7A7A',
  },
};
function priorityFigma(priority: string): PriorityFigmaStyle {
  return PRIORITY_FIGMA[priority] ?? PRIORITY_FIGMA.opportunistic;
}

// Figma UC bubbles have 4 concentric filled discs. Radii ratios read from the
// exported SVG (Ellipse 982/979/980/981 have r = 1.0 / 0.79 / 0.44 / 0.16 × outer,
// plus a fatter Recommended-style variant with 979 at 0.86 and 980 at 0.55).
// Averaging both gives a set that reads correctly against both light and dense
// palettes without needing per-priority tuning.
const UC_RING_RADIUS_RATIOS: [number, number, number, number] = [1.0, 0.82, 0.5, 0.23];
const UC_NEW_ASSET_DOT_R = 6;

// Account View — Figma-pastel fills keyed by freshness.
const ACCOUNT_FRESHNESS_FIGMA: Record<string, string> = {
  active: '#7FE3AA',
  cooling: '#FFCB7D',
  stale: '#FF9FA8',
};
function accountColorFigma(freshness: string): string {
  return ACCOUNT_FRESHNESS_FIGMA[freshness] ?? '#B7CEDB';
}

// Asset View — 4 stage fills keyed by AssetSummary.maturity.
interface AssetMaturityFigmaStyle {
  fill: string;
  alpha: number;
}
const ASSET_MATURITY_FIGMA: Record<string, AssetMaturityFigmaStyle> = {
  SCALING:   { fill: '#F7B7AC', alpha: 0.4 },
  PLAYBOOK:  { fill: '#FFCB7E', alpha: 0.4 },
  BUILDING:  { fill: '#F8DE7F', alpha: 0.4 },
  EXPLORING: { fill: '#CCCBAD', alpha: 0.4 },
};
function assetMaturityFigma(maturity: string | undefined): AssetMaturityFigmaStyle {
  const key = (maturity ?? '').toUpperCase();
  return ASSET_MATURITY_FIGMA[key] ?? ASSET_MATURITY_FIGMA.EXPLORING;
}
// Outline for `origin = ivt_incubated` assets on Asset View — 4-stop blue
// gradient. Canvas doesn't support stroke gradients directly, so we build a
// CanvasGradient on each draw.
const NEW_ASSET_OUTLINE_STOPS: [number, string][] = [
  [0.0,      '#00567B'],
  [0.326923, '#B9C2FA'],
  [0.663462, '#8BB8E2'],
  [1.0,      '#135470'],
];

// Asset View LEGEND swatches — deliberately more saturated than the bubble
// fill palette so 10×8 legend chips still read as distinct rows.
const ASSET_MATURITY_LEGEND: Record<string, string> = {
  SCALING:   'rgba(250, 38, 0, 0.3)',
  PLAYBOOK:  'rgba(255, 153, 0, 0.5)',
  BUILDING:  'rgba(249, 211, 76, 0.7)',
  EXPLORING: '#CCCBAD',
};

// ── View-mode + filter-state types ─────────────────────────────────────────

type ViewMode = 'industry' | 'usecase' | 'asset' | 'account';

const VIEW_MODES: { id: ViewMode; label: string; title: string }[] = [
  { id: 'industry', label: 'Industry View', title: 'Industry Pipeline' },
  { id: 'usecase', label: 'Use Case View', title: 'Use Case Pipeline' },
  { id: 'asset', label: 'Asset View', title: 'Asset Pipeline' },
  { id: 'account', label: 'Account View', title: 'Account Pipeline' },
];

// Sub-toggle rendered below the 4-pill row. See analysisMode state below the
// component for what each mode swaps out.
type AnalysisMode = 'engagement' | 'maturity';

const ANALYSIS_MODES: { id: AnalysisMode; label: string }[] = [
  { id: 'engagement', label: 'Engagement Depth View' },
  { id: 'maturity', label: 'Realized vs. Potential View' },
];

// Win Rate coloring for Realized-vs-Potential mode. Palette from Figma
// node 3231:45451 (Impact page Realized-vs-Potential mock, 2026-08-06).
// `unresolved` = neither launched nor closed-lost (all opps still open) →
// the "Open Pipelines Only" band (orange, called out separately from the
// green WR gradient so operators immediately read it as "no realized
// outcome yet").
const WIN_RATE_BANDS = {
  unresolved: '#F4A672', // 🟧 Open Pipelines Only — no closed pipelines yet
  wr0to24:   '#D8ECE0', // 🟢 WR 0–24%
  wr25to49:  '#8FDDBA', // 🟢 WR 25–49%
  wr50to74:  '#3BC48B', // 🟢 WR 50–74%
  wr75to100: '#0DAA6B', // 🟢 WR 75–100%
} as const;

/**
 * Client-side Win Rate band for a bubble. Denominator = launchedCount +
 * closedLostCount ("closed pipelines"). WR = launchedCount / denominator.
 * Zero denominator → "unresolved" band (nothing closed yet, all opps still
 * open — colored separately so it doesn't collapse into WR 0%). Matches
 * the Figma legend chip order.
 */
function winRateBand(b: { launchedCount: number; closedLostCount: number }): keyof typeof WIN_RATE_BANDS {
  const closed = b.launchedCount + b.closedLostCount;
  if (closed <= 0) return 'unresolved';
  const wr = b.launchedCount / closed;
  if (wr < 0.25) return 'wr0to24';
  if (wr < 0.50) return 'wr25to49';
  if (wr < 0.75) return 'wr50to74';
  return 'wr75to100';
}

function winRateColor(b: { launchedCount: number; closedLostCount: number }): string {
  return WIN_RATE_BANDS[winRateBand(b)];
}

// ── Overall-industry baseline (DAT-8875) ───────────────────────────────────
// The single crosshair on the chart. The pre-existing subset-mean crosshair
// (slate, short dashes) was removed on PM feedback — one reference line, not
// two. Violet + long dashes so it reads as a reference annotation rather than
// as data.
const BASELINE_COLOR = '#5F4BB6';
const BASELINE_DASH: [number, number] = [9, 5];

// What one bubble IS at each grain, so the baseline's population is never
// ambiguous. The scope ("all industries") is the same in every view but the
// averaging grain is not, which is the whole reason the number changes when you
// switch view — spelling the grain out next to the number ("avg use case") makes
// that self-explanatory instead of looking like a bug.
const BASELINE_GRAIN_WORD: Record<ViewMode, string> = {
  industry: 'industries',
  usecase: 'use cases',
  asset: 'assets',
  account: 'accounts',
};
const BASELINE_GRAIN_SINGULAR: Record<ViewMode, string> = {
  industry: 'industry',
  usecase: 'use case',
  asset: 'asset',
  account: 'account',
};

// Y-axis / bubble-size readers. Realized-vs-Potential mode swaps Y from ARR to
// the open-opp count and size from opp count to ARR. Hoisted to module scope
// (they used to be closures inside drawChartFigma) because the baseline point
// is computed in the component — for the legend text — and MUST use the exact
// same readers the canvas plots with, or the crosshair and the legend would
// disagree.
function chartYOf(b: Bubble, isMaturity: boolean): number {
  return isMaturity ? (b.openCount ?? 0) : b.totalArr;
}
function chartSizeOf(b: Bubble, isMaturity: boolean): number {
  return isMaturity ? b.totalArr : b.oppCount;
}

const ALL_OPT: SelectProps.Option = { value: '__all__', label: 'All' };

// Per-chart filter state. Each ChartCard owns one of these; the top-level
// component holds an array of them and passes each into the corresponding
// card. New charts inherit their initial state from the previously last card
// (so "+ Add New Chart" defaults to a duplicate the user can then tweak).
interface ChartFilterState {
  id: string;
  industryOpt: SelectProps.Option;
  ucOpt: SelectProps.Option;
  assetOpt: SelectProps.Option;
  /**
   * Cloudscape PropertyFilter query for the Pipeline类型 slot. Replaces the
   * old single-value `pipelineTypeOpt` (Select of 5 hardcoded buckets) with a
   * one-level AND/OR tree over SFDC_STAGE / PIPELINE_SCORE / CREATED_DATE /
   * CLOSE_DATE / IS_BIG_BET / HAS_IVT_ACTIVITY (see impactPipelineFilter.ts).
   * Serialized to `pipelineFilter` JSON on GetPipelineBubbles.
   */
  pipelineFilterQuery: PropertyFilterProps.Query;
  snapshotDate: string | undefined;
  chartMode: 'chart' | 'table';
  /**
   * Analysis mode toggle: Engagement Depth (default, historic behavior) or
   * Realized-vs-Potential. Per-chart so multi-chart compares can pit
   * different modes side by side. viewMode (the 4-pill above) stays shared
   * across cards — the pill row is a shell-level control, the sub-toggle
   * belongs to the chart card.
   */
  analysisMode: AnalysisMode;
  /**
   * Show the overall-industry baseline crosshair (DAT-8875). Default ON — the
   * baseline is the reference a scoped view is meant to be read against, so it
   * has to be there without the PM having to find a switch. Per-chart like the
   * other chart-scoped controls, so a compare pair can show one chart with the
   * baseline and one without.
   *
   * NOT available on Account View: the baseline needs the same grain with the
   * industry scoping removed, and GetPipelineBubbles rejects ACCOUNT grain
   * without an `industry` (400 InvalidInputException, thrown in
   * UcPipelineActivities.getPipelineBubbles), so there is no cross-industry
   * account population to average. The whole control + legend is hidden there
   * rather than greyed out — a disabled checkbox only raises a question the UI
   * can't answer. Enabling it needs that backend validation relaxed.
   */
  showBaseline: boolean;
}

/** PM's default (2026-09-07): a chart starts scoped to own-industry rows.
 *
 *  Clearing the token sticks, and more thoroughly than it might look: `onChange` patches the
 *  existing chart state and never re-runs this factory, and `addChart` clones the LAST chart
 *  (`{...last, id}`) rather than defaulting, so a new chart inherits whatever the user has
 *  now. This factory is reached only when the page mounts with no charts. Nothing here is
 *  persisted, matching the rest of this page's chart state, so a reload does return to the
 *  default — unlike the Tagged Pipeline tab, whose query is in sessionStorage.
 *
 *  ⚠️ The token has NO effect on the ACCOUNT grain: groupBy=account routes to
 *  findAccountCrossUc, which never receives pipelineFilter. That predates this field and
 *  applies to every tree field; left as-is by decision, raised with the owning team. */
const DEFAULT_PIPELINE_FILTER_QUERY = ownIndustryOnlyQuery;

// PropertyFilter config (filteringProperties / filteringOptions / i18n) lives
// inside <IpoPipelinePropertyFilter> — see that component for the field
// allowlist and candidate value pools.
function makeDefaultChartState(id: string): ChartFilterState {
  return {
    id,
    industryOpt: ALL_OPT,
    ucOpt: ALL_OPT,
    assetOpt: ALL_OPT,
    pipelineFilterQuery: DEFAULT_PIPELINE_FILTER_QUERY(),
    snapshotDate: undefined,
    chartMode: 'chart',
    analysisMode: 'engagement',
    showBaseline: true,
  };
}

// Randomness is unavailable in workflow scripts but this is regular runtime
// code — Math.random() is fine here. Prefix keeps the id readable in devtools.
function newChartId(): string {
  return 'chart-' + Math.random().toString(36).slice(2, 9);
}

/**
 * Drill-down modifier — Shift OR Cmd (macOS) OR Ctrl (Windows/Linux).
 * A modifier + click on an industry bubble jumps to Use Case View with the
 * industry filter applied; on a UC bubble jumps to Asset View with
 * industry+UC applied. Asset / Account / Ghost bubbles don't participate.
 *
 * Any of the three modifiers works so users don't have to remember which OS
 * key maps to "modifier action" — Shift is the most discoverable, meta/ctrl
 * mirror the OS convention for "special-action click" (open in new tab etc).
 */
function isDrillModifier(e: React.MouseEvent): boolean {
  return e.shiftKey || e.metaKey || e.ctrlKey;
}

// ── Component ─────────────────────────────────────────────────────────────

export default function IndustryPipelineOverview() {
  // Permission gate — same three-state fail-CLOSED machine used by MotionsPage
  // (loading/error/ready). "All" here means "All my allowed industries", i.e.
  // the client filters GetPipelineBubbles results to the allowed set. Since
  // GetPipelineBubbles only accepts a single `industry` query param, the wire
  // request is still all-industries and the ACL is a client-side UX guard —
  // real server-side scoping is a follow-up backend change.
  const { uid } = useAuth();
  const [permission, setPermission] = useState<{
    status: 'loading' | 'error' | 'ready';
    allowed: string[];
  }>({ status: 'loading', allowed: [] });
  useEffect(() => {
    if (!uid) {
      setPermission({ status: 'error', allowed: [] });
      return;
    }
    let cancelled = false;
    setPermission({ status: 'loading', allowed: [] });
    (async () => {
      try {
        const result = await fetchAllowedIndustries(uid);
        if (cancelled) return;
        setPermission(
          result.ok
            ? { status: 'ready', allowed: result.industries }
            : { status: 'error', allowed: [] },
        );
      } catch {
        // fail-CLOSED — permissionClient already returns {ok:false} on
        // caught failures, but wrap the IIFE too so any surprising throw
        // (upstream refactor, setPermission itself, etc.) can't leave the
        // page stuck on loading forever.
        if (!cancelled) setPermission({ status: 'error', allowed: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);
  const allowedIndustries = permission.allowed;
  const permissionError = permission.status === 'error';
  const permissionLoading = permission.status === 'loading';

  // View mode is card-shared across all charts.
  const [viewMode, setViewMode] = useState<ViewMode>('industry');
  // Sub-toggle (Engagement Depth vs. Realized-vs-Potential) is per-chart —
  // lives on ChartFilterState (see `analysisMode` field). This lets a user
  // put two chart cards side by side, one in each mode, for A/B compare.
  // Engagement mode is the historical behavior (X = influence density /
  // arsenal, Y = totalArr, per-grain coloring). Realized-vs-Potential
  // swaps X = launchedCount + closedLostCount, Y = openCount, size =
  // totalArr, color = client-derived Win Rate (5 bands including "Open
  // Pipelines Only"). Applies uniformly to all four grains.
  // Charts — start with one; user can add up to N. When the array has
  // length > 1, we're in "compare mode": each chart gets a Delete link,
  // and clicking any bubble triggers the "Unable to View Details" modal
  // instead of opening a detail panel (Figma: only single-chart supports drill).
  const [charts, setCharts] = useState<ChartFilterState[]>(() => [
    makeDefaultChartState(newChartId()),
  ]);

  // Selected bubble → drives the detail panel below the (single) card.
  // Multi-chart mode short-circuits the click handler to open a modal
  // instead of setting this, so this only ever holds a bubble in single mode.
  const [selectedBubble, setSelectedBubble] = useState<Bubble | null>(null);
  // Modal shown when the user clicks a bubble while multiple charts are open.
  const [showBlockDetailModal, setShowBlockDetailModal] = useState(false);

  // ── Asset list (New Asset UC keys + Asset View dropdown options + Asset
  // View bubble metadata) — ONE fetch, shared across all charts ────────
  const [naUcKeys, setNaUcKeys] = useState<Set<string>>(new Set());
  const [allAssets, setAllAssets] = useState<AssetSummary[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const client = getIvtClient();
        const out = await client.send(new ListAssetsCommand({}));
        const assets = (out.assets ?? []) as AssetSummary[];
        const keys = new Set<string>();
        for (const a of assets) {
          if (
            a.origin === AssetOrigin.IVT_INCUBATED
            && a.industry
            && a.useCase
          ) {
            keys.add(`${a.industry}::${a.useCase}`);
          }
        }
        if (!cancelled) {
          setNaUcKeys(keys);
          setAllAssets(assets);
        }
      } catch {
        if (!cancelled) {
          setNaUcKeys(new Set());
          setAllAssets([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isMulti = charts.length > 1;

  const updateChart = useCallback((id: string, patch: Partial<ChartFilterState>) => {
    setCharts((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }, []);

  const addChart = useCallback(() => {
    // New chart inherits its filter/snapshot from the last existing chart
    // so users get a clean duplicate they can then tweak — the whole point
    // of compare mode is to change ONE thing (usually snapshotDate).
    setCharts((cs) => {
      const last = cs[cs.length - 1];
      return [
        ...cs,
        last
          ? { ...last, id: newChartId() }
          : makeDefaultChartState(newChartId()),
      ];
    });
    // Adding a chart transitions single → multi; clear the currently-open
    // detail panel because multi mode doesn't support drill-in.
    setSelectedBubble(null);
  }, []);

  const deleteChart = useCallback((id: string) => {
    setCharts((cs) => cs.filter((c) => c.id !== id));
    setSelectedBubble(null);
  }, []);

  // The active chart (single mode) — what the detail panel below the card
  // will read filter values from, and the target chart whose filters get
  // rewritten when a drill click is dispatched below.
  const activeChart = charts[0];

  // Bubble click handler passed into each chart. In single-chart mode it
  // opens the detail panel; in multi-chart mode it opens the "Unable to
  // View Details" modal instead. `drill` is true when the click carries a
  // modifier key (Shift / Cmd / Ctrl) AND the bubble kind has a defined
  // drill target — industry drills to Use Case View (industry filter), UC
  // drills to Asset View (industry+UC filter). Asset / Account / Ghost have
  // no drill target and fall through to the normal detail-panel behavior.
  const handleBubbleClick = useCallback(
    (bubble: Bubble, drill: boolean) => {
      if (isMulti) {
        setShowBlockDetailModal(true);
        return;
      }
      if (drill) {
        if (bubble.kind === 'industry') {
          const industry = (bubble as IndustryBubble).industry;
          updateChart(activeChart.id, {
            industryOpt: { value: industry, label: industry },
            ucOpt: ALL_OPT,
            assetOpt: ALL_OPT,
          });
          setViewMode('usecase');
          setSelectedBubble(null);
          return;
        }
        if (bubble.kind === 'uc') {
          const uc = bubble as UcBubble;
          updateChart(activeChart.id, {
            industryOpt: { value: uc.industry, label: uc.industry },
            ucOpt: { value: uc.ucName, label: uc.ucName },
            assetOpt: ALL_OPT,
          });
          setViewMode('asset');
          setSelectedBubble(null);
          return;
        }
        // asset / account / ghost: no drill target — fall through to open
        // the detail panel just like a plain click.
      }
      setSelectedBubble(bubble);
    },
    [isMulti, activeChart, updateChart],
  );

  // View-mode change resets selection + the industry filter default snap.
  // "All" doesn't make sense for the per-industry views, so switching to
  // one of them from an industry with industryOpt=All snaps to the first
  // available industry once data lands. Since each chart has its own
  // industryOpt, we push the reset down to each chart on view-mode change.
  const handleViewModeChange = useCallback((next: ViewMode) => {
    setViewMode(next);
    setSelectedBubble(null);
  }, []);

  // fail-CLOSED: permission fetch failed (service down, no uid, etc.). Do NOT
  // render the charts (which would fall open to every industry via
  // GetPipelineBubbles({})). A permission outage must never expose every
  // industry.
  if (permissionError) {
    return (
      <Alert type="error" header="无法加载 Industry 权限">
        未能获取你可访问的行业。请刷新页面重试；若持续失败请联系管理员。
      </Alert>
    );
  }

  // Permission is settling — hold the page so the first ChartCard doesn't
  // race ahead of the allowed set with an unfiltered fetch.
  if (permissionLoading) {
    return (
      <div className="ivt-usecases-page__loading">
        <Spinner size="large" />
        <span>加载 Industry 权限…</span>
      </div>
    );
  }

  return (
    <>
      <div className="ivt-ipo__card">
        {/* ── Header: title + 4-pill segmented selector ────────────── */}
        <div className="ivt-ipo__header">
          <div className="ivt-ipo__title">
            {VIEW_MODES.find((m) => m.id === viewMode)?.title ?? 'Pipeline'}
          </div>
          <div className="ivt-ipo__pills" role="tablist" aria-label="Pipeline view mode">
            {VIEW_MODES.map((m, i) => (
              <span key={m.id} className="ivt-ipo__pill-wrap">
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === m.id}
                  className={`ivt-ipo__pill${viewMode === m.id ? ' ivt-ipo__pill--active' : ''}`}
                  onClick={() => handleViewModeChange(m.id)}
                >
                  {m.label}
                </button>
                {i < VIEW_MODES.length - 1 && <span className="ivt-ipo__pill-sep">|</span>}
              </span>
            ))}
          </div>
        </div>

        {/* Analysis-mode sub-toggle now lives inside each ChartCard's
             chart-area, on the same row as the Y-axis label. Applies to
             whichever of the 4 views is selected above; state is still
             shared across all chart cards (via analysisMode below). */}

        {/* ── One or more chart cards ──────────────────────────────── */}
        {charts.map((chart, idx) => (
          <ChartCard
            key={chart.id}
            chart={chart}
            index={idx}
            isMulti={isMulti}
            viewMode={viewMode}
            selectedBubble={selectedBubble}
            naUcKeys={naUcKeys}
            allAssets={allAssets}
            allowedIndustries={allowedIndustries}
            onChange={(patch) => updateChart(chart.id, patch)}
            onAdd={addChart}
            onDelete={() => deleteChart(chart.id)}
            onBubbleClick={handleBubbleClick}
          />
        ))}
      </div>

      {/* ── Detail panel below the card — SINGLE-CHART ONLY ─────────── */}
      {!isMulti && selectedBubble && activeChart && (
        <ActiveDetailPanel
          bubble={selectedBubble}
          chart={activeChart}
          onClose={() => setSelectedBubble(null)}
        />
      )}

      {/* ── Multi-chart bubble-click modal (Figma nodes 2788-29820 popup) ─ */}
      <Modal
        visible={showBlockDetailModal}
        header="Unable to View Details"
        onDismiss={() => setShowBlockDetailModal(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              variant="link"
              onClick={() => setShowBlockDetailModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowBlockDetailModal(false)}
            >
              Ok
            </Button>
          </div>
        }
      >
        Close one table first to view details.
      </Modal>
    </>
  );
}

// ── Detail-panel dispatcher ───────────────────────────────────────────────

/**
 * Picks the right detail panel component for the selected bubble kind and
 * threads filters from the active chart into it. Only rendered in single-chart
 * mode — multi-chart mode intercepts bubble clicks with the block modal.
 */
function ActiveDetailPanel({
  bubble,
  chart,
  onClose,
}: {
  bubble: Bubble;
  chart: ChartFilterState;
  onClose: () => void;
}) {
  // Detail panels still receive a `pipelineTypeFilter?: string` prop for the
  // legacy single-value bucket filter (undefined here — the drill panels don't
  // render it and the backend echoes it back). The property-tree filter
  // (`pipelineFilter`) IS threaded through: the drill-in serializes the SAME
  // query the chart aggregated over, so the opp set the panel shows matches
  // the opp count on the bubble that launched it.
  const pipelineTypeFilter: string | undefined = undefined;
  const pipelineFilter = queryToImpactFilterParam(chart.pipelineFilterQuery);
  const industryScope =
    chart.industryOpt.value === ALL_OPT.value ? '' : (chart.industryOpt.value ?? '');

  if (bubble.kind === 'industry') {
    return (
      <IndustryDetailPanel
        bubble={bubble as IndustryBubble}
        pipelineTypeFilter={pipelineTypeFilter}
        pipelineFilter={pipelineFilter}
        snapshotDate={chart.snapshotDate}
        onClose={onClose}
      />
    );
  }
  if (bubble.kind === 'uc') {
    return (
      <UseCaseDetailPanel
        industry={(bubble as UcBubble).industry}
        bubble={bubble as UcBubble}
        pipelineTypeFilter={pipelineTypeFilter}
        pipelineFilter={pipelineFilter}
        snapshotDate={chart.snapshotDate}
        onClose={onClose}
      />
    );
  }
  if (bubble.kind === 'asset') {
    return (
      <AssetDetailPanel
        bubble={bubble as AssetBubble}
        pipelineTypeFilter={pipelineTypeFilter}
        pipelineFilter={pipelineFilter}
        snapshotDate={chart.snapshotDate}
        onClose={onClose}
      />
    );
  }
  if (bubble.kind === 'account') {
    return (
      <AccountDetailPanel
        industry={industryScope}
        account={bubble as AccountBubble}
        pipelineTypeFilter={pipelineTypeFilter}
        pipelineFilter={pipelineFilter}
        snapshotDate={chart.snapshotDate}
        onClose={onClose}
      />
    );
  }
  if (bubble.kind === 'ghost') {
    return (
      <GhostDetailPanel
        bubble={bubble as GhostBubble}
        pipelineTypeFilter={pipelineTypeFilter}
        pipelineFilter={pipelineFilter}
        snapshotDate={chart.snapshotDate}
        onClose={onClose}
      />
    );
  }
  return null;
}

// ── ChartCard ─────────────────────────────────────────────────────────────

/**
 * One "chart card" — a self-contained filter row + canvas that fetches its
 * own data. The parent shell owns viewMode, the assets list, and the array
 * of ChartFilterStates. This component owns everything else: drill fetches,
 * per-view caches, snapshotDate discovery, and canvas rendering.
 */
function ChartCard({
  chart,
  index,
  isMulti,
  viewMode,
  selectedBubble,
  naUcKeys,
  allAssets,
  allowedIndustries,
  onChange,
  onAdd,
  onDelete,
  onBubbleClick,
}: {
  chart: ChartFilterState;
  index: number;
  isMulti: boolean;
  viewMode: ViewMode;
  /** Selected bubble in single-chart mode — drives the Table row radio.
   *  In multi-chart mode this stays null (parent short-circuits clicks to
   *  the block-detail modal instead of setting selection). */
  selectedBubble: Bubble | null;
  naUcKeys: Set<string>;
  allAssets: AssetSummary[];
  /**
   * Settled allowed industry set (parent gates on permission = ready before
   * mounting this card, so `allowedIndustries` is always the final answer here
   * — MAY be legitimately empty when the user has no industry permission).
   * "All" in this card means "All my allowed industries": we filter the
   * GetPipelineBubbles response to this set and restrict every dropdown
   * (Industry, Asset) to codes intersected with it. Client-side UX guard,
   * not a security boundary — the backend doesn't re-verify.
   */
  allowedIndustries: string[];
  onChange: (patch: Partial<ChartFilterState>) => void;
  onAdd: () => void;
  onDelete: () => void;
  /** `drill=true` means the click carried a modifier key (Shift/Cmd/Ctrl)
   *  and the parent should attempt a drill-down instead of opening the
   *  detail panel. Parent decides whether the bubble kind actually supports
   *  drilling — kinds without a drill target fall through to the panel. */
  onBubbleClick: (bubble: Bubble, drill: boolean) => void;
}) {
  const allowedSet = useMemo(() => new Set(allowedIndustries), [allowedIndustries]);
  const hasAnyAllowed = allowedIndustries.length > 0;
  const {
    industryOpt,
    ucOpt,
    assetOpt,
    pipelineFilterQuery,
    snapshotDate,
    chartMode,
    analysisMode,
    showBaseline,
  } = chart;
  const isMaturity = analysisMode === 'maturity';

  // Server-side pipelineFilter (JSON property tree) — undefined = no filter.
  // The query is translated to the backend contract via impactPipelineFilter,
  // then JSON.stringify'd inside queryToImpactFilterParam.
  const activePipelineFilter = useMemo(
    () => queryToImpactFilterParam(pipelineFilterQuery),
    [pipelineFilterQuery],
  );

  // Every response carries availableSnapshotDates; latest response wins.
  const [availableSnapshotDates, setAvailableSnapshotDates] = useState<string[]>([]);
  const availableSet = useMemo(
    () => new Set(availableSnapshotDates),
    [availableSnapshotDates],
  );

  // ── Overview data (industry roll-up — always loaded, feeds Industry view AND
  // the industryOptions list for all views) ──────────────────────────────
  const [raw, setRaw] = useState<PipelineBubble[] | null>(null);
  const [snapshotAt, setSnapshotAt] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    // fail-CLOSED short-circuit: user has NO industry permission → don't
    // fetch (backend has no industries-array filter, so we'd have to pull
    // all-industries and then drop everything client-side — worse UX and
    // extra load). Render as an empty overview.
    if (!hasAnyAllowed) {
      setRaw([]);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const client = getIvtClient();
        const out = await client.send(
          new GetPipelineBubblesCommand({
            // groupBy=industry: backend aggregates opps DISTINCT per (industry,
            // opp_id) so a cross-UC opp counts once — matches drill-in
            // GetUcPipelineDetail semantics. Previously we defaulted to
            // groupBy=USE_CASE and rolled up client-side via aggregateByIndustry,
            // which double-counted opps tagged for multiple UCs (LS_HC bubble
            // said 148 opps while drill-in showed 138).
            groupBy: 'industry',
            pipelineFilter: activePipelineFilter,
            snapshotDate,
          }),
        );
        if (cancelled) return;
        // Client-side scope to allowed industries — see ChartCard prop doc.
        const items = (out.items ?? []).filter(
          (b) => !b.industry || allowedSet.has(b.industry),
        );
        setRaw(items);
        setSnapshotAt(out.snapshotDate);
        if (out.availableSnapshotDates?.length) {
          setAvailableSnapshotDates(out.availableSnapshotDates);
          // First render: snapshotDate is still undefined. Adopt the
          // backend's today (availableSnapshotDates[0]) as the picker default
          // so the DatePicker value matches what the response was served from.
          if (snapshotDate === undefined) {
            onChange({ snapshotDate: out.availableSnapshotDates[0] });
          }
        }
      } catch (e) {
        if (cancelled) return;
        setError((e as Error).message ?? 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, activePipelineFilter, snapshotDate, hasAnyAllowed, allowedSet]);

  // ── Per-view drill fetches (Use Case + Account + Asset) ────────────────
  interface LiveData {
    items: PipelineBubble[];
    hasInfluence: boolean;
    snapshotAt?: string;
  }
  interface AcctData {
    items: PipelineBubble[];
    insights: AccountInsights | null;
  }
  const [liveCache, setLiveCache] = useState<Record<string, LiveData>>({});
  const [acctCache, setAcctCache] = useState<Record<string, AcctData>>({});
  const [assetCache, setAssetCache] = useState<Record<string, LiveData>>({});
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState<string | null>(null);

  const availableIndustries = useMemo(() => {
    if (!raw) return [] as string[];
    const set = new Set<string>();
    for (const r of raw) if (r.industry) set.add(r.industry);
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'en'));
  }, [raw]);

  // When switching to a per-industry view with "All" selected, snap to the
  // first available industry.
  useEffect(() => {
    if (viewMode !== 'industry' && industryOpt.value === ALL_OPT.value) {
      if (availableIndustries.length > 0) {
        const first = availableIndustries[0];
        onChange({ industryOpt: { value: first, label: first } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, availableIndustries]);

  const drillIndustry =
    viewMode === 'industry'
      ? null
      : industryOpt.value === ALL_OPT.value
        ? null
        : (industryOpt.value ?? null);
  const drillUcName =
    viewMode === 'asset' && ucOpt.value !== ALL_OPT.value ? (ucOpt.value ?? null) : null;

  // Cache key uses the serialized property-filter (JSON string) so any tree
  // change invalidates the drill cache; an empty filter reduces to "__all__"
  // so requests without a tree share the cache slot.
  const pipelineFilterKey = activePipelineFilter ?? ALL_OPT.value;
  const snapshotKey = snapshotDate ?? '';
  const liveCacheKey = drillIndustry
    ? `${drillIndustry}::${pipelineFilterKey}::${snapshotKey}`
    : '';
  const assetCacheKey =
    viewMode === 'asset'
      ? `${drillIndustry ?? ''}::${drillUcName ?? ''}::${pipelineFilterKey}::${snapshotKey}`
      : '';
  const acctCacheKey = drillIndustry
    ? `${drillIndustry}::${pipelineFilterKey}::${snapshotKey}`
    : '';

  const liveCached = drillIndustry ? !!liveCache[liveCacheKey] : true;
  const acctCached = drillIndustry ? !!acctCache[acctCacheKey] : true;
  const assetCached = viewMode === 'asset' ? !!assetCache[assetCacheKey] : true;

  useEffect(() => {
    if (viewMode === 'usecase' || viewMode === 'account') {
      if (drillIndustry === null) return;
    } else if (viewMode !== 'asset') {
      return;
    }
    // Defense-in-depth: dropdowns are already constrained to allowed
    // industries, but a stale industryOpt (e.g. from a persisted or shared
    // URL) could still request an out-of-scope drill. Refuse it.
    if (drillIndustry && !allowedSet.has(drillIndustry)) {
      setDrillLoading(false);
      setDrillError(null);
      return;
    }
    const ind = drillIndustry;
    const cached =
      viewMode === 'usecase' ? liveCached : viewMode === 'account' ? acctCached : assetCached;
    if (cached) {
      setDrillLoading(false);
      setDrillError(null);
      return;
    }
    let cancelled = false;
    setDrillLoading(true);
    setDrillError(null);
    (async () => {
      try {
        const client = getIvtClient();
        if (viewMode === 'usecase') {
          const out = await client.send(
            new GetPipelineBubblesCommand({
              industry: ind!,
              pipelineFilter: activePipelineFilter,
              snapshotDate,
            }),
          );
          if (cancelled) return;
          setLiveCache((c) => ({
            ...c,
            [liveCacheKey]: {
              items: out.items ?? [],
              hasInfluence: !!out.hasInfluence,
              snapshotAt: out.snapshotDate,
            },
          }));
          if (out.availableSnapshotDates?.length) {
            setAvailableSnapshotDates(out.availableSnapshotDates);
          }
        } else if (viewMode === 'asset') {
          const [assetOut, ucOut] = await Promise.all([
            client.send(
              new GetPipelineBubblesCommand({
                groupBy: 'asset',
                industry: ind ?? undefined,
                ucName: drillUcName ?? undefined,
                pipelineFilter: activePipelineFilter,
                snapshotDate,
              }),
            ),
            ind
              ? client.send(
                  new GetPipelineBubblesCommand({
                    industry: ind,
                    pipelineFilter: activePipelineFilter,
                    snapshotDate,
                  }),
                )
              : Promise.resolve(null),
          ]);
          if (cancelled) return;
          setAssetCache((c) => ({
            ...c,
            [assetCacheKey]: {
              items: assetOut.items ?? [],
              hasInfluence: !!assetOut.hasInfluence,
              snapshotAt: assetOut.snapshotDate,
            },
          }));
          if (assetOut.availableSnapshotDates?.length) {
            setAvailableSnapshotDates(assetOut.availableSnapshotDates);
          }
          if (ucOut && ind) {
            setLiveCache((c) => ({
              ...c,
              [`${ind}::${activePipelineFilter ?? ALL_OPT.value}::${snapshotKey}`]: {
                items: ucOut.items ?? [],
                hasInfluence: !!ucOut.hasInfluence,
                snapshotAt: ucOut.snapshotDate,
              },
            }));
          }
        } else {
          // Account View — GetPipelineBubbles(groupBy=ACCOUNT). Unified with
          // the Industry / UC / Asset views onto one op; the deprecated
          // GetAccountCrossUc is retained on the wire (see @deprecated on
          // Smithy) and will be removed by a follow-up cleanup CR. Insights
          // are derived on the client so PMs can retune the five thresholds
          // in `deriveAccountInsights` without a service deploy.
          const out = await client.send(
            new GetPipelineBubblesCommand({
              groupBy: 'account',
              industry: ind!,
              pipelineFilter: activePipelineFilter,
              snapshotDate,
            }),
          );
          if (cancelled) return;
          const items = out.items ?? [];
          const insights = deriveAccountInsights(accountBubbles(items));
          setAcctCache((c) => ({ ...c, [acctCacheKey]: { items, insights } }));
          if (out.availableSnapshotDates?.length) {
            setAvailableSnapshotDates(out.availableSnapshotDates);
          }
        }
      } catch (e) {
        if (cancelled) return;
        setDrillError((e as Error).message ?? 'Unknown error');
      } finally {
        if (!cancelled) setDrillLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    drillIndustry,
    drillUcName,
    liveCacheKey,
    assetCacheKey,
    acctCacheKey,
    viewMode,
    activePipelineFilter,
    snapshotDate,
    snapshotKey,
    liveCached,
    acctCached,
    assetCached,
  ]);

  // ── Bubbles for current view ───────────────────────────────────────────
  // Every industry in the overview response, BEFORE the `industryOpt` filter.
  // Industry View's visible set is a subset of this; the baseline averages over
  // all of it. Backend groupBy=INDUSTRY returns one per-industry aggregate row,
  // so this maps straight into IndustryBubble — no client-side rollup, no
  // double-count. The pipe/arsenal filter (total_arr>0 || arsenal_count>0) is
  // enforced server-side via the ars CTE + LEFT JOIN in findIndustryBubbles, so
  // industries with no signal are already absent.
  const allIndustryBubbles = useMemo<IndustryBubble[]>(() => {
    if (!raw) return [];
    return raw
      .filter((r) => !!r.industry)
      .map((r) => ({
        kind: 'industry' as const,
        industry: r.industry!,
        arsenalCount: r.arsenalCount ?? 0,
        oppCount: r.oppCount ?? 0,
        totalArr: r.totalArr ?? 0,
        launchedArr: r.launchedArr ?? 0,
        launchedCount: r.launchedCount ?? 0,
        closedLostCount: r.closedLostCount ?? 0,
        openCount: r.openCount ?? 0,
        ucCount: r.ucCount ?? 0,
        influenceDensityOpp: r.influenceDensityOpp ?? 0,
      }));
  }, [raw]);

  const liveData = drillIndustry ? liveCache[liveCacheKey] : undefined;
  const acctData = drillIndustry ? acctCache[acctCacheKey] : undefined;
  const hasInfluence =
    viewMode === 'asset'
      ? (assetCache[assetCacheKey]?.hasInfluence ?? false)
      : (liveData?.hasInfluence ?? false);

  const bubbles: Bubble[] = useMemo(() => {
    if (!raw) return [];
    if (viewMode === 'industry') {
      const all = allIndustryBubbles;
      if (industryOpt.value === ALL_OPT.value) return all;
      return all.filter((b) => b.industry === industryOpt.value);
    }
    if (viewMode === 'usecase') {
      if (!drillIndustry || !liveData) return [];
      const ucs = ucBubblesFromLive(liveData.items, drillIndustry);
      const present = new Set(ucs.map((u) => `${u.industry}::${u.ucName}`));
      const ghosts: GhostBubble[] = [];
      for (const key of naUcKeys) {
        const sep = key.indexOf('::');
        const ind = key.slice(0, sep);
        const ucName = key.slice(sep + 2);
        if (ind === drillIndustry && !present.has(key)) {
          ghosts.push({
            kind: 'ghost',
            industry: ind,
            ucName,
            arsenalCount: 0,
            oppCount: 0,
            totalArr: 0,
            launchedArr: 0,
            launchedCount: 0,
            closedLostCount: 0,
            openCount: 0,
          });
        }
      }
      let filtered: Bubble[] = [...ucs, ...ghosts];
      if (ucOpt.value !== ALL_OPT.value) {
        filtered = filtered.filter((b) =>
          b.kind === 'uc' ? b.ucName === ucOpt.value : b.kind === 'ghost' ? b.ucName === ucOpt.value : false,
        );
      }
      return filtered;
    }
    if (viewMode === 'account') {
      if (!drillIndustry || !acctData) return [];
      return accountBubbles(acctData.items);
    }
    if (viewMode === 'asset') {
      const data = assetCache[assetCacheKey];
      if (!data) return [];
      let assets = assetBubblesFromPipeline(data.items);
      if (assetOpt.value !== ALL_OPT.value) {
        assets = assets.filter((a) => a.assetId === assetOpt.value);
      }

      // NA-UC ghost bubbles — Figma 20260605 draft, left side of the chart.
      // Semantic (per-opp): each ghost aggregates the pipeline in a UC
      // that is NOT covered by any asset-level tag. Signals to the PM
      // where asset tagging is missing so the pipeline can flow into a
      // real asset bubble instead of showing up as a ghost.
      //
      // Two-tier ghost logic:
      //
      //   1. UC completely uncovered — no asset in this UC has any
      //      matching pipeline. Ghost carries the full UC totals.
      //   2. UC partially covered — the UC has some asset bubbles, but
      //      not every opp is covered by an asset-level tag. Ghost
      //      carries the ORPHAN portion: (UC totals − Σ per-asset totals).
      //
      // The orphan approximation subtracts per-asset totals (from the
      // asset-grain response) from UC totals. When an opp carries
      // multiple asset-level tags in one UC it double-counts in the
      // subtrahend, which can push the orphan slightly below the true
      // per-opp count (by at most the tag-fanout size, ~0..2 per UC in
      // prod). Ghost drops when the residual reaches zero. A precise
      // per-opp orphan count is a follow-up backend field.
      //
      // Skipped entirely when the asset-id filter is active — that's a
      // single-bubble focus, ghosts would be confusing there.
      if (assetOpt.value === ALL_OPT.value && liveData) {
        // Per-UC roll-up of the currently visible asset bubbles.
        // `assets` is already filtered to the current scope so the sum
        // matches what the user sees.
        const perUcAssetOpps = new Map<string, number>();
        const perUcAssetArr = new Map<string, number>();
        const perUcAssetLaunchedArr = new Map<string, number>();
        for (const a of assets) {
          if (!a.ucName) continue;
          perUcAssetOpps.set(a.ucName, (perUcAssetOpps.get(a.ucName) ?? 0) + (a.oppCount ?? 0));
          perUcAssetArr.set(a.ucName, (perUcAssetArr.get(a.ucName) ?? 0) + (a.totalArr ?? 0));
          perUcAssetLaunchedArr.set(
            a.ucName,
            (perUcAssetLaunchedArr.get(a.ucName) ?? 0) + (a.launchedArr ?? 0),
          );
        }

        const ghosts: GhostBubble[] = [];
        const ucOpt_ = ucOpt.value === ALL_OPT.value ? null : ucOpt.value;
        const seenGhostUc = new Set<string>();
        for (const it of liveData.items) {
          if (!it.ucName || !it.industry) continue;
          const ucTotalArr = it.totalArr ?? 0;
          if (ucTotalArr <= 0) continue;
          if (ucOpt_ && it.ucName !== ucOpt_) continue;
          if (seenGhostUc.has(it.ucName)) continue;
          seenGhostUc.add(it.ucName);

          const assetOpps = perUcAssetOpps.get(it.ucName) ?? 0;
          const assetArr = perUcAssetArr.get(it.ucName) ?? 0;
          const assetLaunched = perUcAssetLaunchedArr.get(it.ucName) ?? 0;

          const orphanOpps = Math.max(0, (it.oppCount ?? 0) - assetOpps);
          const orphanArr = Math.max(0, ucTotalArr - assetArr);
          const orphanLaunchedArr = Math.max(0, (it.launchedArr ?? 0) - assetLaunched);

          // Only emit when there's a residual — a UC that's fully covered
          // by its asset bubbles (orphanOpps === 0 AND orphanArr === 0)
          // shouldn't show a ghost.
          if (orphanOpps <= 0 && orphanArr <= 0) continue;

          ghosts.push({
            kind: 'ghost',
            industry: it.industry,
            ucName: it.ucName,
            arsenalCount: 0,
            oppCount: orphanOpps,
            totalArr: orphanArr,
            launchedArr: orphanLaunchedArr,
            // Ghost lifecycle counts aren't computed today (the orphan
            // subtraction is on totals only). Left as 0 — Realized-vs-Potential
            // mode places ghost bubbles at the origin, which is fine because
            // ghosts already convey the "no asset tag" signal via their
            // dashed outline in the left ghost column.
            launchedCount: 0,
            closedLostCount: 0,
            openCount: 0,
          });
        }
        return [...assets, ...ghosts];
      }
      return assets;
    }
    return [];
  }, [
    raw,
    viewMode,
    drillIndustry,
    liveData,
    acctData,
    assetCache,
    assetCacheKey,
    assetOpt.value,
    naUcKeys,
    industryOpt.value,
    ucOpt.value,
    allIndustryBubbles,
  ]);

  // ── Overall-industry baseline population (DAT-8875) ────────────────────
  // Same grain as the chart with the industry / UC / asset scoping dropped, but
  // the pipeline property filter and snapshotDate preserved — the comparison
  // must differ ONLY in scope, not in what counts as a pipeline or in which day
  // is being read.
  //
  //   Industry View — free: `raw` is already the all-industry response, so the
  //                   baseline is `allIndustryBubbles` with no extra request.
  //   Use Case View — GetPipelineBubbles({}) → all-industry UC rows (the Smithy
  //                   contract documents `industry` omitted as exactly this
  //                   all-industry roll-up for the USE_CASE grain).
  //   Asset View    — GetPipelineBubbles({groupBy:'asset'}) with no industry
  //                   and no ucName.
  //   Account View  — NOT AVAILABLE. GetPipelineBubbles rejects ACCOUNT grain
  //                   without an `industry` (400 InvalidInputException, thrown
  //                   in UcPipelineActivities.getPipelineBubbles), so there is
  //                   no cross-industry account population to average without a
  //                   backend change. The toggle is disabled on that view.
  //
  // Fetched whenever the view has a baseline at all — NOT gated on the toggle.
  // The toggle only controls whether the crosshair is drawn; the numbers stay in
  // the legend either way, so the population has to be loaded either way.
  // Cached per (grain, pipelineFilter, snapshot).
  type BaselineGrain = 'usecase' | 'asset';
  const baselineGrain: BaselineGrain | null =
    viewMode === 'usecase' ? 'usecase' : viewMode === 'asset' ? 'asset' : null;
  const baselineKey = baselineGrain
    ? `${baselineGrain}::${pipelineFilterKey}::${snapshotKey}`
    : '';
  const [baselineCache, setBaselineCache] = useState<Record<string, PipelineBubble[]>>({});
  // Failures are tracked PER KEY, mirroring `baselineCache`. A single shared
  // boolean leaked across keys: the effect clears it only after its
  // `baselineCached` early-return, so switching to an already-cached key left a
  // previous key's failure set, and switching to a new uncached key showed
  // "(baseline unavailable)" for the render before the effect reset it.
  const [baselineFailedKeys, setBaselineFailedKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // Industry grain never fetches (baselineKey is ''), so it can never have failed.
  const baselineFailed = baselineKey ? baselineFailedKeys.has(baselineKey) : false;
  const baselineCached = baselineKey ? baselineKey in baselineCache : true;
  // "A request for the current key is genuinely in flight" — deliberately NOT
  // "we have no baseline point". A resolved-but-empty population also yields no
  // point (restrictive pipeline filter, or every row dropped by the totalArr>0
  // guards in the bubble mappers), and Industry grain never fetches at all;
  // conflating those with in-flight left the legend stuck on "(loading…)".
  const baselineLoading = baselineGrain !== null && !baselineCached && !baselineFailed;

  useEffect(() => {
    if (!baselineGrain || !hasAnyAllowed || baselineCached) return;
    let cancelled = false;
    (async () => {
      try {
        const client = getIvtClient();
        const out = await client.send(
          new GetPipelineBubblesCommand({
            // No industry / ucName / assetId — this IS the unscoped population.
            // USE_CASE is the server-side default grain, so omit groupBy there.
            groupBy: baselineGrain === 'asset' ? 'asset' : undefined,
            pipelineFilter: activePipelineFilter,
            snapshotDate,
          }),
        );
        if (cancelled) return;
        setBaselineCache((c) => ({ ...c, [baselineKey]: out.items ?? [] }));
        // A retry that succeeds clears the key's earlier failure, so the state
        // can't disagree with the cache it now has data in.
        setBaselineFailedKeys((s) => {
          if (!s.has(baselineKey)) return s;
          const next = new Set(s);
          next.delete(baselineKey);
          return next;
        });
      } catch {
        // The baseline is a reference line, not the chart. A failed baseline
        // fetch must never take the view down — drop it and say so in the
        // legend so the PM knows the reference is missing rather than zero.
        if (!cancelled) {
          setBaselineFailedKeys((s) => (s.has(baselineKey) ? s : new Set(s).add(baselineKey)));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineGrain, baselineKey, baselineCached, hasAnyAllowed, activePipelineFilter, snapshotDate]);

  // Always computed, toggle or not — the legend shows the numbers even when the
  // crosshair is hidden (the toggle is about chart clutter, not about whether
  // the PM gets to see the reference values).
  const baselineBubbles = useMemo<Bubble[] | null>(() => {
    if (viewMode === 'industry') {
      return allIndustryBubbles.length ? allIndustryBubbles : null;
    }
    if (viewMode === 'account') return null;
    const rows = baselineCache[baselineKey];
    if (!rows) return null;
    // The unscoped fetch spans EVERY industry, including ones this user can't
    // see. Re-apply the same client-side ACL guard the overview response gets
    // so a baseline never averages in out-of-scope industries.
    const scoped = rows.filter((r) => !r.industry || allowedSet.has(r.industry));
    if (viewMode === 'asset') return assetBubblesFromPipeline(scoped);
    // USE_CASE grain: each row carries its own `industry`, but
    // ucBubblesFromLive pins one industry per call — group, then flatten.
    const byIndustry = new Map<string, PipelineBubble[]>();
    for (const r of scoped) {
      const ind = r.industry ?? '';
      if (!ind) continue;
      const list = byIndustry.get(ind);
      if (list) list.push(r);
      else byIndustry.set(ind, [r]);
    }
    const out: Bubble[] = [];
    for (const [ind, rs] of byIndustry) out.push(...ucBubblesFromLive(rs, ind));
    return out;
  }, [viewMode, allIndustryBubbles, baselineCache, baselineKey, allowedSet]);

  // Filter dropdown options.
  const industryOptions = useMemo<SelectProps.Option[]>(() => {
    const opts: SelectProps.Option[] = [];
    if (viewMode === 'industry') opts.push(ALL_OPT);
    for (const i of availableIndustries) opts.push({ value: i, label: i });
    return opts;
  }, [availableIndustries, viewMode]);

  const ucOptions = useMemo<SelectProps.Option[]>(() => {
    const opts: SelectProps.Option[] = [ALL_OPT];
    if ((viewMode === 'usecase' || viewMode === 'asset') && liveData) {
      const seen = new Set<string>();
      for (const it of liveData.items) {
        if (it.ucName && !seen.has(it.ucName)) {
          seen.add(it.ucName);
          opts.push({ value: it.ucName, label: it.ucName });
        }
      }
    }
    return opts;
  }, [viewMode, liveData]);

  const assetOptions = useMemo<SelectProps.Option[]>(() => {
    const opts: SelectProps.Option[] = [ALL_OPT];
    const industryScope = industryOpt.value === ALL_OPT.value ? null : industryOpt.value;
    const ucScope = ucOpt.value === ALL_OPT.value ? null : ucOpt.value;
    const seen = new Set<string>();
    for (const a of allAssets) {
      if (!a.assetId) continue;
      // Restrict to assets whose industry is in the user's allowed set — an
      // asset without an industry falls through (rare bookkeeping case).
      if (a.industry && !allowedSet.has(a.industry)) continue;
      if (industryScope && a.industry !== industryScope) continue;
      if (ucScope && a.useCase !== ucScope) continue;
      if (seen.has(a.assetId)) continue;
      seen.add(a.assetId);
      opts.push({ value: a.assetId, label: a.name ?? a.assetId });
    }
    return opts;
  }, [allAssets, industryOpt.value, ucOpt.value, allowedSet]);

  // No pipelineTypeOptions any more — the old 5-bucket single-Select is
  // replaced by the PropertyFilter below, whose filteringProperties + candidate
  // value pools live in module-scope constants (IMPACT_FILTERING_PROPERTIES /
  // IMPACT_FILTERING_OPTIONS) so they aren't rebuilt each render.

  const axis: AxisCfg = useMemo(() => {
    // Realized vs. Potential mode: X = launchedCount + closedLostCount
    // ("closed pipelines"), same across all four grains. Y and size are
    // handled separately in drawChartFigma (yOf / sizeOf overrides) — this
    // AxisCfg is X-only per the existing PipelineMap contract.
    if (analysisMode === 'maturity') {
      return {
        valueOf: (b) => {
          if (b.kind === 'ghost') return 0;
          return (b.launchedCount ?? 0) + (b.closedLostCount ?? 0);
        },
        title: 'Closed Pipelines',
        word: 'Closed',
        isInfluence: false,
        xLabel: 'Closed',
      };
    }
    if (viewMode === 'industry') {
      return {
        valueOf: (b) => (b as IndustryBubble).influenceDensityOpp,
        title: X_AXIS_LABELS.opp_norm,
        word: 'Influence',
        isInfluence: true,
        xLabel: X_AXIS_LABELS.opp_norm,
      };
    }
    if (viewMode === 'account') {
      return {
        valueOf: (b) => (b as AccountBubble).influenceDensityOpp,
        title: X_AXIS_LABELS.opp_norm,
        word: 'Influence',
        isInfluence: true,
        xLabel: X_AXIS_LABELS.opp_norm,
      };
    }
    if (viewMode === 'asset') {
      const useInf = hasInfluence;
      return {
        valueOf: (b) => {
          if (b.kind !== 'asset') return 0;
          return useInf ? (b as AssetBubble).influenceDensityOpp : (b as AssetBubble).arsenalCount;
        },
        title: useInf ? X_AXIS_LABELS.opp_norm : 'Arsenal Count',
        word: useInf ? 'Influence' : 'Arsenal',
        isInfluence: useInf,
        xLabel: useInf ? X_AXIS_LABELS.opp_norm : 'Arsenal',
      };
    }
    // usecase
    const useInf = hasInfluence;
    return {
      valueOf: (b) => {
        if (b.kind !== 'uc') return 0;
        return useInf ? (b as UcBubble).influenceDensityOpp : (b as UcBubble).arsenalCount;
      },
      title: useInf ? X_AXIS_LABELS.opp_norm : 'Arsenal Count',
      word: useInf ? 'Influence' : 'Arsenal',
      isInfluence: useInf,
      xLabel: useInf ? X_AXIS_LABELS.opp_norm : 'Arsenal',
    };
  }, [analysisMode, viewMode, bubbles, hasInfluence]);

  // Baseline crosshair position, in data coordinates. Uses the SAME axis readers
  // the canvas plots with so the drawn lines and the legend numbers can't drift.
  const baselinePt = useMemo(
    () => baselinePoint(baselineBubbles, axis.valueOf, (b) => chartYOf(b, isMaturity)),
    [baselineBubbles, axis, isMaturity],
  );

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const layoutRef = useRef<BubbleLayout[]>([]);
  const naKeysSnapshot = naUcKeys;

  // Hover-list popover — replaces the old single-hit tooltip. Semantics:
  //   - hover on any bubble(s) → popover shows a compact list of every hit,
  //     positioned near the cursor (A3/E1). Even a single hit gets a popover
  //     (B1) — the old inline tooltip is gone.
  //   - click → locks the current hover popover in place (A3). Locked
  //     popover survives mouseleave and only closes via its × button or
  //     click on empty canvas.
  //   - row click → forwards to onBubbleClick (multi-chart mode's existing
  //     "Unable to View Details" modal still fires there).
  //   - hit-testing reuses hitBubbles() as-is; ghost bubbles and data
  //     bubbles share one popover (D1).
  const [hoverList, setHoverList] = useState<HoverListState | null>(null);
  // hoverList changes trigger re-render; keep the "current hits key" in a ref
  // so mousemove can skip setState when the hit set is stable — critical for
  // 60fps hover smoothness.
  const hoverKeyRef = useRef<string>('');

  const assetMetaById = useMemo(() => {
    const m = new Map<string, { maturity?: string; isNew: boolean }>();
    for (const a of allAssets) {
      if (a.assetId) {
        m.set(a.assetId, {
          maturity: a.maturity,
          isNew: a.origin === AssetOrigin.IVT_INCUBATED,
        });
      }
    }
    return m;
  }, [allAssets]);

  useEffect(() => {
    if (loading) return;
    if (chartMode !== 'chart') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const draw = () => {
      if (!canvasRef.current) return;
      layoutRef.current = drawChartFigma(
        canvasRef.current,
        bubbles,
        viewMode === 'industry',
        axis,
        viewMode === 'usecase' ? naKeysSnapshot : undefined,
        viewMode === 'asset' ? assetMetaById : undefined,
        analysisMode,
        // Toggle governs the CROSSHAIR only (and, with it, whether the baseline
        // is folded into the axis spans). The legend keeps showing the numbers.
        showBaseline ? baselinePt : null,
      );
    };
    draw();
    const observer = new ResizeObserver(() => draw());
    observer.observe(canvas);
    window.addEventListener('resize', draw);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', draw);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bubbles,
    viewMode,
    analysisMode,
    axis,
    loading,
    chartMode,
    assetMetaById,
    baselinePt,
    showBaseline,
  ]);

  const handleCanvasMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const ex = e.clientX - rect.left;
      const ey = e.clientY - rect.top;
      const hits = hitBubbles(layoutRef.current, ex, ey);
      canvas.style.cursor = hits.length ? 'pointer' : 'default';

      // Never displace a locked popover on hover — the whole point of locking
      // is that the user can move the mouse toward the popover to click a row.
      setHoverList((prev) => {
        if (prev?.locked) return prev;
        if (hits.length === 0) {
          if (prev === null) return prev;
          hoverKeyRef.current = '';
          return null;
        }
        // Follow the cursor with px/py always, only re-issue the bubbles array
        // when the hit set actually changes (keeps popover from re-rendering
        // its row list on every mouse pixel).
        const key = bubbleHitKey(hits);
        if (key === hoverKeyRef.current && prev) {
          return { ...prev, px: ex, py: ey };
        }
        hoverKeyRef.current = key;
        return { bubbles: hits, px: ex, py: ey, locked: false };
      });
    },
    [],
  );

  const handleCanvasMouseLeave = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) canvas.style.cursor = 'default';
    // Locked popover survives mouseleave — user is probably moving the cursor
    // TO the popover to click a row. Only clear the unlocked hover state.
    setHoverList((prev) => {
      if (prev?.locked) return prev;
      hoverKeyRef.current = '';
      return null;
    });
  }, []);

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const ex = e.clientX - rect.left;
      const ey = e.clientY - rect.top;
      const hits = hitBubbles(layoutRef.current, ex, ey);
      if (hits.length === 0) {
        // Click on empty canvas closes any locked popover.
        setHoverList(null);
        hoverKeyRef.current = '';
        return;
      }
      // Single-hit bubble → no ambiguity, so click routes straight to the
      // parent handler. Bare click opens the detail panel; modifier + click
      // triggers the drill-down (industry/uc only; asset/account/ghost fall
      // through to the panel). The popover was a preview during hover — once
      // the user commits with a click, close it and act.
      const drill = isDrillModifier(e);
      if (hits.length === 1) {
        setHoverList(null);
        hoverKeyRef.current = '';
        onBubbleClick(hits[0], drill);
        return;
      }
      // Multi-hit bubble → lock the popover so the user can disambiguate by
      // clicking a specific row (rows also accept modifier + click for drill).
      hoverKeyRef.current = bubbleHitKey(hits);
      setHoverList({ bubbles: hits, px: ex, py: ey, locked: true });
    },
    [onBubbleClick],
  );

  const handlePopoverRowClick = useCallback(
    (b: Bubble, drill: boolean) => {
      // Close popover first so multi-chart's "Unable to View Details" modal
      // (opened inside onBubbleClick) doesn't fight for focus.
      setHoverList(null);
      hoverKeyRef.current = '';
      onBubbleClick(b, drill);
    },
    [onBubbleClick],
  );

  const handlePopoverClose = useCallback(() => {
    setHoverList(null);
    hoverKeyRef.current = '';
  }, []);

  // Notes describe what the current mode's bubble encoding means. In
  // Realized-vs-Potential mode the size encoding switches to totalArr and
  // the UC's concentric relevance rings are flattened to a solid WR band,
  // so the engagement-mode notes don't apply.
  // Account View can't have a cross-industry baseline — see the ChartFilterState
  // doc on `showBaseline`. Hides the toggle + legend outright on that view.
  const baselineDisabled = viewMode === 'account';
  const baselineGrainWord = BASELINE_GRAIN_WORD[viewMode];
  const baselineGrainSingular = BASELINE_GRAIN_SINGULAR[viewMode];

  const noteText =
    analysisMode === 'maturity'
      ? '* Bubble size represents Pipeline ARR — larger bubbles carry more ARR.'
      : viewMode === 'usecase'
        ? '* Each circle has 4 color rings from light to dark, representing engagement depth levels 0-3 (weak to strong).'
        : '* Bubble size represents pipeline volume — larger bubbles indicate more pipelines.';

  if (loading) {
    return (
      <div className="ivt-usecases-page__loading">
        <Spinner size="large" />
        <span>加载 Pipeline Overview…</span>
      </div>
    );
  }
  if (error) {
    return (
      <Alert
        type="error"
        header="无法加载 Pipeline Overview"
        action={<button onClick={() => setReloadKey((k) => k + 1)}>重试</button>}
      >
        {error}
      </Alert>
    );
  }
  // Legit zero-permission user — permission service answered but returned no
  // industries. Show an empty-state banner rather than a blank chart so the
  // user knows why nothing is here.
  if (!hasAnyAllowed) {
    return (
      <Alert type="info" header="无 Industry 权限">
        当前账号没有任何 Industry 的查看权限，Impact 页面无数据可展示。如需权限请联系管理员。
      </Alert>
    );
  }

  return (
    <div className="ivt-ipo__chart-block">
      {/* Table N header — shown only in multi-chart mode per Figma
          (2842:33427, 2788:29820). Single-chart mode has no header
          because "Table 1" alone reads as visual noise. */}
      {isMulti && (
        <div className="ivt-ipo__chart-header">Table {index + 1}</div>
      )}

      {/* ── Filter row ───────────────────────────────────────────────
          Extracted to <IpoFilters>. Pipeline类型 sits as the FIRST cell of
          the row (per Figma 3186-43468); its applied tokens overflow to a
          second visual line via CSS (see .ivt-ipo__pipeline-filter-row).
          Chart/Table toggle and + Add / − Delete Chart controls are card-
          scoped, not filters — they slot into the row's right edge. */}
      <IpoFilters
        leftSlot={
          <IpoPipelinePropertyFilter
            query={pipelineFilterQuery}
            onChange={(q) => onChange({ pipelineFilterQuery: q })}
          />
        }
        industryOpt={industryOpt}
        onIndustryChange={(opt) => onChange({ industryOpt: opt })}
        industryOptions={industryOptions}
        ucOpt={ucOpt}
        onUcChange={(opt) => onChange({ ucOpt: opt })}
        ucOptions={ucOptions}
        ucDisabled={viewMode !== 'usecase' && viewMode !== 'asset'}
        assetOpt={assetOpt}
        onAssetChange={(opt) => onChange({ assetOpt: opt })}
        assetOptions={assetOptions}
        assetDisabled={viewMode !== 'asset'}
        snapshotDate={snapshotDate}
        onSnapshotDateChange={(d) => onChange({ snapshotDate: d })}
        // Gate to backend-materialised snapshot dates once we know them.
        // Empty set means the first response hasn't landed yet — allow every
        // date through so the picker isn't fully greyed-out on mount.
        isDateEnabled={(date) =>
          availableSet.size === 0 || availableSet.has(formatIsoDate(date))
        }
        snapshotPlaceholder={snapshotAt ?? 'YYYY/MM/DD'}
        rightSlot={
          <>
            {/* Chart/Table toggle */}
            <div className="ivt-ipo__chart-toggle" role="radiogroup" aria-label="Chart or table">
              <button
                type="button"
                role="radio"
                aria-checked={chartMode === 'chart'}
                className={`ivt-ipo__chart-toggle-btn${chartMode === 'chart' ? ' ivt-ipo__chart-toggle-btn--active' : ''}`}
                onClick={() => onChange({ chartMode: 'chart' })}
              >
                Chart
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={chartMode === 'table'}
                className={`ivt-ipo__chart-toggle-btn${chartMode === 'table' ? ' ivt-ipo__chart-toggle-btn--active' : ''}`}
                onClick={() => onChange({ chartMode: 'table' })}
              >
                Table
              </button>
            </div>
            {/* Add / Delete affordance — single-chart shows "+ Add New Chart"
                only on the ONE chart; multi-chart shows "- Delete chart" on
                every chart. Kept in the same slot per Figma. */}
            {isMulti ? (
              <button
                type="button"
                className="ivt-ipo__delete-chart-btn"
                onClick={onDelete}
              >
                − Delete chart
              </button>
            ) : (
              <button
                type="button"
                className="ivt-ipo__add-chart-btn"
                onClick={onAdd}
              >
                + Add New Chart
              </button>
            )}
          </>
        }
      />

      {/* ── Chart area ─────────────────────────────────────────────── */}
      <div className="ivt-ipo__chart-area">
        <div className="ivt-ipo__axis-label ivt-ipo__axis-label--y">
          {analysisMode === 'maturity' ? 'Y-Open Pipelines' : 'Y-Pipeline ARR'}
          <span className="ivt-ipo__axis-caret" aria-hidden="true">▼</span>
        </div>
        {/* Analysis-mode sub-toggle — same row as the Y-axis label, right side. */}
        <div
          className="ivt-ipo__analysis-toggle"
          role="radiogroup"
          aria-label="Analysis mode"
        >
          {ANALYSIS_MODES.map((m) => (
            <label
              key={m.id}
              className={`ivt-ipo__analysis-radio${
                analysisMode === m.id ? ' ivt-ipo__analysis-radio--active' : ''
              }`}
            >
              <input
                type="radio"
                name={`analysis-mode-${chart.id}`}
                value={m.id}
                checked={analysisMode === m.id}
                onChange={() => onChange({ analysisMode: m.id })}
              />
              <span>{m.label}</span>
            </label>
          ))}
        </div>

        <div className="ivt-ipo__canvas-wrap">
          {drillError ? (
            <Alert type="error" header="无法加载数据">
              {drillError}
            </Alert>
          ) : drillLoading ? (
            <div className="ivt-usecases-page__loading">
              <Spinner />{' '}
              <span>
                加载
                {viewMode === 'asset'
                  ? ' Asset '
                  : viewMode === 'account'
                    ? drillIndustry + ' 账户 '
                    : drillIndustry + ' UC '}
                数据…
              </span>
            </div>
          ) : chartMode === 'table' ? (
            <BubblesTable
              bubbles={bubbles}
              viewMode={viewMode}
              selectedBubble={selectedBubble}
              // Table mode has no natural place for a "hold Shift to drill"
              // affordance — row click always opens the detail panel. Drill
              // stays a Chart-mode-only interaction.
              onSelect={(b) => onBubbleClick(b, false)}
              hasInfluence={hasInfluence}
            />
          ) : (
            <>
              <canvas
                ref={canvasRef}
                className="ivt-usecases-page__canvas"
                onClick={handleCanvasClick}
                onMouseMove={handleCanvasMouseMove}
                onMouseLeave={handleCanvasMouseLeave}
              />
              {hoverList && (
                <BubbleHoverList
                  state={hoverList}
                  axis={axis}
                  assetMetaById={viewMode === 'asset' ? assetMetaById : undefined}
                  onRowClick={handlePopoverRowClick}
                  onClose={handlePopoverClose}
                />
              )}
            </>
          )}
        </div>

        <div className="ivt-ipo__axis-label ivt-ipo__axis-label--x">
          {analysisMode === 'maturity' ? 'X-Launched / Closed' : 'X-Pipeline平均参与深度'}
          <span className="ivt-ipo__axis-caret" aria-hidden="true">▼</span>
        </div>
        <div className="ivt-ipo__note">
          <span>{noteText}</span>

          {/* ── Overall-industry baseline: toggle + legend (DAT-8875) ─────
              Hidden entirely on Account View (`baselineDisabled`) — that grain
              has no baseline at all, and a greyed-out control there just raises
              a question it can't answer. Chart-mode only besides: a crosshair
              has no meaning in the table.

              The checkbox hides the CROSSHAIR; the numbers stay visible either
              way so the reference values are always readable. The grain is
              spelled out beside them ("avg use case") because the scope is
              identical across views while the averaging grain is not — that's
              why the number changes when you switch view. */}
          {chartMode === 'chart' && !baselineDisabled && (
            <span className="ivt-ipo__legend">
              <label
                className="ivt-ipo__baseline-toggle"
                title={
                  `Reference crosshair at the mean of ALL ${baselineGrainWord}`
                  + ' (industry scope dropped; same pipeline filter and snapshot date).'
                }
              >
                <input
                  type="checkbox"
                  checked={showBaseline}
                  onChange={(e) => onChange({ showBaseline: e.target.checked })}
                />
                <span
                  className="ivt-ipo__legend-dash"
                  style={{ borderTopColor: BASELINE_COLOR }}
                  aria-hidden="true"
                />
                <span>Overall industry baseline</span>
              </label>
              <span
                className="ivt-ipo__legend-chip"
                title={
                  'Averaged over ALL industries you can see, at this view\'s grain'
                  + ` (one row = one ${baselineGrainSingular}). Same scope in every view;`
                  + ' the grain is what changes, so the number does too.'
                }
              >
                {baselineChipText(baselinePt, {
                  grainSingular: baselineGrainSingular,
                  yIsMoney: !isMaturity,
                  xIsInfluence: axis.isInfluence,
                  loading: baselineLoading,
                  failed: baselineFailed,
                })}
              </span>
            </span>
          )}
          {analysisMode === 'maturity' && (
            <span className="ivt-ipo__legend">
              <span className="ivt-ipo__legend-chip">
                <span
                  className="ivt-ipo__legend-swatch"
                  style={{ background: WIN_RATE_BANDS.unresolved }}
                />{' '}
                Open Pipelines Only
              </span>
              <span className="ivt-ipo__legend-sep">|</span>
              <span className="ivt-ipo__legend-chip">Closed Pipeline Win Rate:</span>
              <span className="ivt-ipo__legend-chip">
                <span
                  className="ivt-ipo__legend-swatch"
                  style={{ background: WIN_RATE_BANDS.wr0to24 }}
                />{' '}
                WR 0%-24%
              </span>
              <span className="ivt-ipo__legend-chip">
                <span
                  className="ivt-ipo__legend-swatch"
                  style={{ background: WIN_RATE_BANDS.wr25to49 }}
                />{' '}
                WR 25%-49%
              </span>
              <span className="ivt-ipo__legend-chip">
                <span
                  className="ivt-ipo__legend-swatch"
                  style={{ background: WIN_RATE_BANDS.wr50to74 }}
                />{' '}
                WR 50%-74%
              </span>
              <span className="ivt-ipo__legend-chip">
                <span
                  className="ivt-ipo__legend-swatch"
                  style={{ background: WIN_RATE_BANDS.wr75to100 }}
                />{' '}
                WR 75%-100%
              </span>
            </span>
          )}
          {analysisMode === 'engagement' && viewMode === 'usecase' && (
            <span className="ivt-ipo__legend">
              <span className="ivt-ipo__legend-chip">
                <span
                  className="ivt-ipo__legend-dot"
                  style={{ background: PRIORITY_FIGMA.strategic.ringInnerBoost }}
                />{' '}
                Strategic
              </span>
              <span className="ivt-ipo__legend-chip">
                <span
                  className="ivt-ipo__legend-dot"
                  style={{ background: PRIORITY_FIGMA.recommended.ringInnerBoost }}
                />{' '}
                Recommended
              </span>
              <span className="ivt-ipo__legend-chip">
                <span
                  className="ivt-ipo__legend-dot"
                  style={{ background: PRIORITY_FIGMA.opportunistic.ringInnerBoost }}
                />{' '}
                Opportunistic
              </span>
              <span className="ivt-ipo__legend-sep">|</span>
              <span className="ivt-ipo__legend-chip">
                <span
                  className="ivt-ipo__legend-dot ivt-ipo__legend-dot--sm"
                  style={{ background: PRIORITY_FIGMA.strategic.dotColor }}
                />
                <span
                  className="ivt-ipo__legend-dot ivt-ipo__legend-dot--sm"
                  style={{ background: PRIORITY_FIGMA.recommended.dotColor }}
                />
                <span
                  className="ivt-ipo__legend-dot ivt-ipo__legend-dot--sm"
                  style={{ background: PRIORITY_FIGMA.opportunistic.dotColor }}
                />{' '}
                New Asset
              </span>
            </span>
          )}
          {analysisMode === 'engagement' && viewMode === 'asset' && (
            <span className="ivt-ipo__legend">
              <span className="ivt-ipo__legend-chip">
                <span
                  className="ivt-ipo__legend-swatch"
                  style={{ background: ASSET_MATURITY_LEGEND.SCALING }}
                />{' '}
                规模化
              </span>
              <span className="ivt-ipo__legend-chip">
                <span
                  className="ivt-ipo__legend-swatch"
                  style={{ background: ASSET_MATURITY_LEGEND.PLAYBOOK }}
                />{' '}
                打法期
              </span>
              <span className="ivt-ipo__legend-chip">
                <span
                  className="ivt-ipo__legend-swatch"
                  style={{ background: ASSET_MATURITY_LEGEND.BUILDING }}
                />{' '}
                建设期
              </span>
              <span className="ivt-ipo__legend-chip">
                <span
                  className="ivt-ipo__legend-swatch"
                  style={{ background: ASSET_MATURITY_LEGEND.EXPLORING }}
                />{' '}
                探索期
              </span>
              <span className="ivt-ipo__legend-chip ivt-ipo__legend-chip--new-asset">
                <span
                  className="ivt-ipo__legend-bar"
                  style={{
                    background: `linear-gradient(to right, ${NEW_ASSET_OUTLINE_STOPS
                      .map(([stop, color]) => `${color} ${stop * 100}%`)
                      .join(', ')})`,
                  }}
                />{' '}
                Blue bars indicate new Assets
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Account view industry-level insights callouts. Kept inside the
          chart block so it flows with the chart it describes. Only shows
          on Account view and only in single-chart mode — multi-chart mode
          doesn't drill in, and the insights bar visually reads as part of
          the drill hierarchy. */}
      {!isMulti &&
        viewMode === 'account' &&
        !drillLoading &&
        !drillError &&
        acctData?.insights && (
          <AccountInsightsBar industry={drillIndustry!} insights={acctData.insights} />
        )}
    </div>
  );
}

// DatePicker hands `isDateEnabled` a JS Date at local-midnight for each cell.
// The backend's availableSnapshotDates are ISO YYYY-MM-DD strings without a
// timezone — comparing by toISOString() would silently shift by up to a day
// for users west of UTC. Format from the Date's local parts to match the
// picker's own semantics.
function formatIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Convert #rrggbb hex + alpha into rgba() for the concentric-disc fills.
function colorWithAlpha(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// Wrap `text` into at most `maxLines` lines within `maxWidth` pixels.
function wrapLabel(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  if (!text) return [''];
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const next = cur ? cur + ' ' + w : w;
    if (ctx.measureText(next).width <= maxWidth) {
      cur = next;
    } else {
      if (cur) lines.push(cur);
      cur = w;
      if (lines.length === maxLines - 1) break;
    }
  }
  if (cur && lines.length < maxLines) lines.push(cur);
  if (lines.length === maxLines) {
    const rejoined = lines.join(' ');
    if (rejoined.length < text.length) {
      let tail = lines[maxLines - 1];
      while (tail.length > 0 && ctx.measureText(tail + '…').width > maxWidth) {
        tail = tail.slice(0, -1);
      }
      lines[maxLines - 1] = tail + '…';
    }
  }
  return lines;
}

/**
 * Figma-style drawChart. Fork of PipelineMap.drawChart for the redesigned Use
 * Cases page. See original for full commentary — this version is unchanged
 * from the pre-refactor code.
 */
function drawChartFigma(
  canvas: HTMLCanvasElement,
  bubbles: Bubble[],
  isOverview: boolean,
  axis: AxisCfg,
  naKeys: Set<string> | undefined,
  assetMeta: Map<string, { maturity?: string; isNew: boolean }> | undefined,
  analysisMode: AnalysisMode,
  /** Overall-industry baseline in data coordinates, or null to skip it
   *  (toggle off, Account View, or the baseline population was empty). */
  baseline: BaselinePoint | null,
): BubbleLayout[] {
  // Realized-vs-Potential mode swaps 3 aspects of the plot uniformly across
  // all four grains: Y = openCount (was totalArr), bubble size = totalArr
  // (was oppCount), fill = client-derived Win Rate band (was per-grain
  // color). X is already switched in the axis useMemo above (launchedCount +
  // closedLostCount). Engagement Depth mode is unchanged — the readers below
  // fall back to the historical fields.
  const isMaturity = analysisMode === 'maturity';
  const yOf = (b: Bubble): number => chartYOf(b, isMaturity);
  const sizeOf = (b: Bubble): number => chartSizeOf(b, isMaturity);
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, w, h);

  if (!bubbles.length) {
    ctx.fillStyle = '#64748b';
    ctx.font = '12px "Amazon Ember", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('No pipeline data', w / 2, h / 2);
    return [];
  }

  const ghosts = bubbles.filter((b): b is GhostBubble => b.kind === 'ghost');
  const dataBubbles = bubbles.filter((b) => b.kind !== 'ghost');

  // Asset View gets a dedicated left column for the NA-UC ghost bubbles so
  // they can carry their own Y scale + size scale independent of the data
  // side (see ghost-render block below). Canvas layout when the column is
  // active:
  //
  //   [ghost tick gutter] [ghost column] [main tick gutter] [main plot]
  //         44px               120px           40px            rest
  //
  // Ghost Y-axis tick labels sit in the LEFT gutter so they don't collide
  // with ghost bubbles/labels. Main Y-axis tick labels sit in the RIGHT
  // gutter between the ghost column and the main plot area, keeping the
  // main-tick right-align math (`pad.left - 8`) working as-is.
  const isAssetView = assetMeta !== undefined;
  const hasGhostColumn = isAssetView && ghosts.length > 0;
  const GHOST_TICK_GUTTER = 44;
  const GHOST_COL_WIDTH = 120;
  const MAIN_TICK_GUTTER = 40;
  const pad = {
    top: 24,
    right: 48,
    bottom: 44,
    left: hasGhostColumn ? GHOST_TICK_GUTTER + GHOST_COL_WIDTH + MAIN_TICK_GUTTER : 60,
  };
  const ghostColLeft = hasGhostColumn ? GHOST_TICK_GUTTER : pad.left;
  const ghostColRight = hasGhostColumn ? GHOST_TICK_GUTTER + GHOST_COL_WIDTH : pad.left;
  const pw = w - pad.left - pad.right;
  const ph = h - pad.top - pad.bottom;
  // The baseline is folded into the axis spans on purpose. Without it, a chart
  // scoped to a small subset can have a span far below the population mean and
  // the baseline would land off-canvas — a reference line you can't see is
  // worse than none. Both are per-item means at the same grain, so their
  // magnitudes are comparable and the rescale stays mild; a PM who wants the
  // tight subset-only scale back turns the baseline off.
  const baseX = baseline?.x ?? 0;
  const baseY = baseline?.y ?? 0;
  const fullMaxX =
    Math.max(...dataBubbles.map((b) => axis.valueOf(b)), baseX, axis.isInfluence ? 0.1 : 1) * 1.3;
  const fullMaxY = Math.max(...dataBubbles.map((b) => yOf(b)), baseY, 1) * 1.25;
  const spanX = fullMaxX || 1;
  const spanY = fullMaxY || 1;
  const maxR = Math.max(...dataBubbles.map((b) => sizeOf(b)), 1);
  const scaleR = (v: number) =>
    isOverview
      ? Math.max(32, Math.sqrt(v / maxR) * 82)
      : Math.max(40, Math.sqrt(v / maxR) * 80);

  ctx.strokeStyle = FIGMA_AXIS_LINE_COLOR;
  ctx.lineWidth = 1;

  ctx.font = '14px "Amazon Ember", sans-serif';
  ctx.fillStyle = FIGMA_AXIS_LABEL_COLOR;
  ctx.textAlign = 'right';
  for (let i = 0; i <= 4; i++) {
    const y = pad.top + ph - (ph * i) / 4;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(pad.left + pw, y);
    ctx.strokeStyle = FIGMA_AXIS_LINE_COLOR;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = FIGMA_AXIS_LABEL_COLOR;
    // Y label: engagement mode shows dollars ($M); maturity mode's Y is a
    // count of open opps, so plain integer.
    const yLabel = isMaturity
      ? String(Math.round((spanY * i) / 4))
      : '$' + ((spanY * i) / 4 / 1e6).toFixed(0) + ' M';
    ctx.fillText(yLabel, pad.left - 8, y + 5);
  }
  ctx.textAlign = 'center';
  for (let i = 0; i <= 6; i++) {
    const x = pad.left + (pw * i) / 6;
    const xtv = (spanX * i) / 6;
    ctx.fillStyle = FIGMA_AXIS_LABEL_COLOR;
    ctx.fillText(
      axis.isInfluence ? xtv.toFixed(1) : String(Math.round(xtv)),
      x,
      pad.top + ph + 20,
    );
  }

  // Overall-industry baseline crosshair (DAT-8875) — the ONE crosshair on the
  // chart. It replaces the old subset-mean crosshair, which was dropped on PM
  // feedback: two dashed crosshairs read as chart noise, and the mean of
  // whatever happens to be filtered in is the weaker of the two references
  // (it moves with every filter change, so nothing can be compared against it
  // across views). The quadrant captions below sit at fixed 22% / 78% of the
  // plot and were never anchored to the removed lines.
  if (baseline) {
    const bx = pad.left + (baseline.x / spanX) * pw;
    const by = pad.top + ph - (baseline.y / spanY) * ph;
    // Guard: folding the baseline into the spans keeps it inside the plot, but
    // clamp anyway so a surprising value can't paint over the axis gutters.
    const bxc = Math.min(Math.max(bx, pad.left), pad.left + pw);
    const byc = Math.min(Math.max(by, pad.top), pad.top + ph);
    ctx.save();
    ctx.setLineDash(BASELINE_DASH);
    ctx.strokeStyle = BASELINE_COLOR;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(bxc, pad.top);
    ctx.lineTo(bxc, pad.top + ph);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(pad.left, byc);
    ctx.lineTo(pad.left + pw, byc);
    ctx.stroke();
    ctx.setLineDash([]);
    // Diamond at the intersection — the baseline's own "position", so a reader
    // scanning the chart sees one reference point rather than two stray lines.
    const D = 4;
    ctx.beginPath();
    ctx.moveTo(bxc, byc - D);
    ctx.lineTo(bxc + D, byc);
    ctx.lineTo(bxc, byc + D);
    ctx.lineTo(bxc - D, byc);
    ctx.closePath();
    ctx.fillStyle = BASELINE_COLOR;
    ctx.fill();
    // On-canvas label so the line is self-describing without a legend hunt.
    // Sits just above the horizontal baseline at the left edge of the plot,
    // where the quadrant captions (centred at 22% / 78% of the width) aren't.
    ctx.font = '700 10px "Amazon Ember", sans-serif';
    ctx.fillStyle = BASELINE_COLOR;
    ctx.textAlign = 'left';
    ctx.fillText('Overall baseline', pad.left + 4, byc - 5);
    ctx.restore();
  }

  ctx.font = '11px "Amazon Ember", sans-serif';
  ctx.fillStyle = '#94a3b8';
  ctx.textAlign = 'center';
  const qx1 = pad.left + pw * 0.22;
  const qx2 = pad.left + pw * 0.78;
  const qy1 = pad.top + 16;
  const qy2 = pad.top + ph - 8;
  // Quadrant labels: Y word swaps to "Open" in maturity mode (Y = open opps
  // count) instead of the engagement-mode "ARR". X word already comes from
  // the AxisCfg (`Closed` in maturity mode, `Influence`/`Arsenal` otherwise).
  const yWord = isMaturity ? 'Open' : 'ARR';
  ctx.fillText(`⬆ High ${yWord} · Low ${axis.word}`, qx1, qy1);
  ctx.fillText(`⬆ High ${yWord} · High ${axis.word} ★`, qx2, qy1);
  ctx.fillText(`Low ${yWord} · Low ${axis.word}`, qx1, qy2);
  ctx.fillText(`Low ${yWord} · High ${axis.word} →`, qx2, qy2);

  const sorted = [...dataBubbles].sort((a, b) => sizeOf(b) - sizeOf(a));
  const layout: BubbleLayout[] = [];
  for (const b of sorted) {
    const x = pad.left + (axis.valueOf(b) / spanX) * pw;
    const y = pad.top + ph - (yOf(b) / spanY) * ph;
    const r = scaleR(sizeOf(b));
    layout.push({ bubble: b, x, y, r });

    if (isOverview) {
      const industry = (b as IndustryBubble).industry;
      // Maturity mode paints every bubble with the Win Rate band (5 solid
      // colors, no multiply blend). Engagement mode keeps the per-industry
      // pastel + multiply so overlapping industries darken visibly.
      const fill = isMaturity ? winRateColor(b as IndustryBubble) : industryColorFigma(industry);
      ctx.save();
      if (!isMaturity) ctx.globalCompositeOperation = 'multiply';
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.restore();

      ctx.fillStyle = FIGMA_LABEL_COLOR;
      ctx.textAlign = 'center';
      const labelFontSize = r >= 60 ? 18 : r >= 42 ? 15 : 12;
      const bodyFontSize = r >= 60 ? 14 : r >= 42 ? 12 : 10;
      ctx.font = `700 ${labelFontSize}px "Amazon Ember", sans-serif`;
      ctx.fillText(industry, x, y - (labelFontSize * 0.15));
      ctx.font = `400 ${bodyFontSize}px "Amazon Ember", sans-serif`;
      ctx.fillText('$' + (b.totalArr / 1e6).toFixed(1) + 'M', x, y + bodyFontSize + 2);
      ctx.fillText(b.oppCount + ' Opps', x, y + bodyFontSize * 2 + 4);
    } else if (b.kind === 'uc') {
      const uc = b as UcBubble;
      const style = priorityFigma(uc.priority);
      if (isMaturity) {
        // Maturity mode: flat WR band, no concentric priority rings (Win Rate
        // is the coloring semantic, priority no longer maps to fill).
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = winRateColor(uc);
        ctx.fill();
      } else {
        const ratios = UC_RING_RADIUS_RATIOS;
        const alphas = style.ringAlphas;
        ctx.save();
        ctx.globalCompositeOperation = 'multiply';
        for (let i = 0; i < 4; i++) {
          const rr = r * ratios[i];
          const fill = i === 3 ? style.ringInnerBoost : style.ringColor;
          ctx.beginPath();
          ctx.arc(x, y, rr, 0, Math.PI * 2);
          ctx.fillStyle = colorWithAlpha(fill, alphas[i]);
          ctx.fill();
        }
        ctx.restore();
      }

      const name = uc.ucName || bubbleLabel(b);
      const labelFontSize = r >= 60 ? 14 : r >= 40 ? 13 : 11;
      const bodyFontSize = r >= 60 ? 12 : r >= 40 ? 11 : 9;
      ctx.fillStyle = FIGMA_LABEL_COLOR;
      ctx.textAlign = 'center';
      ctx.font = `700 ${labelFontSize}px "Amazon Ember", sans-serif`;
      const lines = wrapLabel(ctx, name, r * 1.9, 2);
      const totalLabelH = lines.length * (labelFontSize + 2) + bodyFontSize * 2 + 4;
      let cursorY = y - totalLabelH / 2 + labelFontSize;
      for (const line of lines) {
        ctx.fillText(line, x, cursorY);
        cursorY += labelFontSize + 2;
      }
      ctx.font = `400 ${bodyFontSize}px "Amazon Ember", sans-serif`;
      ctx.fillText('$' + (uc.totalArr / 1e6).toFixed(1) + 'M', x, cursorY + bodyFontSize - 2);
      ctx.fillText(uc.oppCount + ' Opps', x, cursorY + bodyFontSize * 2);

      // New-Asset satellite dot is Engagement Depth mode only. In RP mode
      // the "new asset" signal is carried entirely by Asset View's blue
      // outline; UC View drops the dot to keep the WR band as the sole
      // visual encoding.
      const key = `${uc.industry}::${uc.ucName}`;
      if (!isMaturity && naKeys && naKeys.has(key)) {
        const dotOffset = r * 0.28;
        const dx = x + dotOffset;
        const dy = y - r * 0.65;
        ctx.beginPath();
        ctx.arc(dx, dy, UC_NEW_ASSET_DOT_R, 0, Math.PI * 2);
        ctx.fillStyle = style.dotColor;
        ctx.fill();
      }
    } else if (b.kind === 'account') {
      const acct = b as AccountBubble;
      const fill = isMaturity ? winRateColor(acct) : accountColorFigma(acct.freshness);
      ctx.save();
      if (!isMaturity) ctx.globalCompositeOperation = 'multiply';
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.restore();

      const labelFontSize = r >= 60 ? 14 : r >= 42 ? 12 : 11;
      const bodyFontSize = r >= 60 ? 12 : r >= 42 ? 11 : 10;
      ctx.fillStyle = FIGMA_LABEL_COLOR;
      ctx.textAlign = 'center';
      ctx.font = `700 ${labelFontSize}px "Amazon Ember", sans-serif`;
      const lines = wrapLabel(ctx, acct.accountName, r * 1.9, 2);
      const totalLabelH = lines.length * (labelFontSize + 2) + bodyFontSize * 2 + 4;
      let cursorY = y - totalLabelH / 2 + labelFontSize;
      for (const line of lines) {
        ctx.fillText(line, x, cursorY);
        cursorY += labelFontSize + 2;
      }
      ctx.font = `400 ${bodyFontSize}px "Amazon Ember", sans-serif`;
      ctx.fillText('$' + (acct.totalArr / 1e6).toFixed(1) + 'M', x, cursorY + bodyFontSize - 2);
      ctx.fillText(acct.oppCount + ' Opps', x, cursorY + bodyFontSize * 2);
    } else if (b.kind === 'asset') {
      const asset = b as AssetBubble;
      const meta = assetMeta?.get(asset.assetId);
      const stageStyle = assetMaturityFigma(meta?.maturity);
      const isNewAsset = meta?.isNew ?? false;

      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      // Maturity mode: solid WR band (opaque). Engagement mode: pastel
      // maturity stage color with alpha. New Asset blue outline still
      // drawn below in either mode.
      ctx.fillStyle = isMaturity
        ? winRateColor(asset)
        : colorWithAlpha(stageStyle.fill, stageStyle.alpha);
      ctx.fill();

      if (isNewAsset) {
        const grad = ctx.createLinearGradient(x - r, y - r, x + r, y + r);
        for (const [stop, color] of NEW_ASSET_OUTLINE_STOPS) grad.addColorStop(stop, color);
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      const labelFontSize = r >= 60 ? 14 : r >= 42 ? 12 : 11;
      ctx.fillStyle = FIGMA_LABEL_COLOR;
      ctx.textAlign = 'center';
      ctx.font = `700 ${labelFontSize}px "Amazon Ember", sans-serif`;
      const name = asset.assetName || bubbleLabel(b);
      const lines = wrapLabel(ctx, name, r * 1.9, 2);
      const totalLabelH = lines.length * (labelFontSize + 2);
      let cursorY = y - totalLabelH / 2 + labelFontSize;
      for (const line of lines) {
        ctx.fillText(line, x, cursorY);
        cursorY += labelFontSize + 2;
      }
    } else {
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = '#64748b25';
      ctx.fill();
      ctx.strokeStyle = '#64748b';
      ctx.lineWidth = 2.5;
      ctx.stroke();
      const name = bubbleLabel(b);
      const label = name.length > 22 ? name.slice(0, 20) + '..' : name;
      ctx.font = '11px "Amazon Ember", sans-serif';
      if (r > 14) {
        ctx.fillStyle = FIGMA_LABEL_COLOR;
        ctx.textAlign = 'center';
        ctx.fillText(label, x, y + 3);
      } else {
        ctx.fillStyle = '#475569';
        ctx.textAlign = 'left';
        ctx.fillText(label, x + r + 4, y + 4);
      }
    }
  }

  ctx.textAlign = 'center';
  const GHOST_COLOR = '#f59e0b';

  // Asset View — ghosts live in a dedicated left column with their own Y
  // scale (uncovered ARR) and size scale (orphan opp count). Both scales are
  // relative to the current ghost set: max ghost anchors the top of the
  // column and the biggest bubble, everything else scales down from there.
  // Ticks are drawn at 0 / mid / max so the reader sees the absolute $M
  // values and doesn't confuse them with the main plot's Y scale.
  const ghostSpanY = hasGhostColumn
    ? Math.max(...ghosts.map((g) => g.totalArr ?? 0), 1) * 1.15
    : 1;
  const ghostMaxOpp = hasGhostColumn
    ? Math.max(...ghosts.map((g) => g.oppCount ?? 0), 1)
    : 1;
  const GHOST_MAX_R = 24;
  const GHOST_MIN_R = 8;
  const scaleGhostR = (opp: number) =>
    Math.max(GHOST_MIN_R, Math.sqrt(opp / ghostMaxOpp) * GHOST_MAX_R);

  if (hasGhostColumn) {
    // Vertical separator between ghost column and main plot area.
    ctx.strokeStyle = FIGMA_AXIS_LINE_COLOR;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(ghostColRight, pad.top);
    ctx.lineTo(ghostColRight, pad.top + ph);
    ctx.stroke();

    // Ghost-column Y ticks at 0 / 0.25 / 0.5 / 0.75 / 1.0 of ghostSpanY.
    // Placed in the LEFT gutter so they don't collide with the ghost circles
    // or their name labels (both sit inside the column itself).
    ctx.font = '10px "Amazon Ember", sans-serif';
    ctx.fillStyle = FIGMA_AXIS_LABEL_COLOR;
    ctx.textAlign = 'right';
    for (let i = 0; i <= 4; i++) {
      const yTick = pad.top + ph - (ph * i) / 4;
      const val = (ghostSpanY * i) / 4;
      ctx.fillText('$' + (val / 1e6).toFixed(1) + 'M', ghostColLeft - 4, yTick + 3);
    }

    // Column caption.
    ctx.font = '10px "Amazon Ember", sans-serif';
    ctx.fillStyle = '#94a3b8';
    ctx.textAlign = 'center';
    const captionX = ghostColLeft + (ghostColRight - ghostColLeft) / 2;
    ctx.fillText('Uncovered ARR', captionX, pad.top - 8);
  }

  ghosts.forEach((g, i) => {
    if (isAssetView) {
      const r = scaleGhostR(g.oppCount ?? 0);
      // Center of the ghost column horizontally.
      const x = ghostColLeft + (ghostColRight - ghostColLeft) / 2;
      // Y position by uncovered ARR — bigger residual sits higher.
      const y = pad.top + ph - ((g.totalArr ?? 0) / ghostSpanY) * ph;
      layout.push({ bubble: g, x, y, r });
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.setLineDash([4, 3]);
      ctx.fillStyle = 'rgba(148,163,184,0.06)';
      ctx.fill();
      ctx.strokeStyle = '#94A3B8';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#64748B';
      ctx.font = `700 ${r >= 20 ? 12 : 11}px "Amazon Ember", sans-serif`;
      const NA_PREFIX = 'NA- ';
      const maxNameLen = 18 - NA_PREFIX.length;
      const name =
        g.ucName.length > maxNameLen ? g.ucName.slice(0, maxNameLen - 1) + '…' : g.ucName;
      ctx.fillText(NA_PREFIX + name, x, y + 4);
    } else {
      const r = 10;
      const x = pad.left + pw - 30;
      const y = pad.top + ph - 20 - i * 28;
      layout.push({ bubble: g, x, y, r });
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.setLineDash([3, 3]);
      ctx.fillStyle = 'rgba(245,158,11,0.08)';
      ctx.fill();
      ctx.strokeStyle = GHOST_COLOR;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = GHOST_COLOR;
      ctx.font = '700 10px "Amazon Ember", sans-serif';
      ctx.fillText('★', x, y + 4);
    }
  });

  // Hover/click handling now lives in React (see ChartCard's handleCanvasMouseMove/
  // handleCanvasClick + <BubbleHoverList>) — canvas is a pure drawing layer.
  return layout;
}

// ── Hover-list popover ────────────────────────────────────────────────────

interface HoverListState {
  bubbles: Bubble[];
  px: number; // cursor x in canvas-local coords
  py: number; // cursor y in canvas-local coords
  locked: boolean;
}

/**
 * Stable identity for a hit set — used by the mousemove handler to avoid
 * re-issuing state updates on every pixel of movement inside the same
 * overlapping bubble cluster. Bubbles have no universal id, so the shape of
 * the concat depends on kind. Order-sensitive on purpose: hitBubbles returns
 * layout order (small→large), which shouldn't jitter as the cursor moves
 * inside a hit cluster.
 */
function bubbleHitKey(bubbles: Bubble[]): string {
  return bubbles.map(bubbleStableId).join('|');
}

function bubbleStableId(b: Bubble): string {
  switch (b.kind) {
    case 'industry': return `i:${b.industry}`;
    case 'uc':       return `u:${b.industry}::${b.ucName}`;
    case 'ghost':    return `g:${b.industry}::${b.ucName}`;
    case 'account':  return `a:${b.accountName}`;
    case 'asset':    return `s:${b.assetId}`;
  }
}

// Row-swatch color for each bubble kind — mirrors the fill palette used by
// the canvas so the popover reads as a legend for the underlying dots.
// Asset maturity lives outside AssetBubble (it's stitched in from the
// ListAssets response via assetMetaById), so the swatch resolver takes that
// map as an optional second arg.
function bubbleSwatchColor(
  b: Bubble,
  assetMetaById?: Map<string, { maturity?: string; isNew: boolean }>,
): string {
  if (b.kind === 'industry') {
    return INDUSTRY_COLORS_FIGMA[b.industry] ?? '#B7CEDB';
  }
  if (b.kind === 'uc') {
    return PRIORITY_FIGMA[b.priority]?.ringInnerBoost
      ?? PRIORITY_FIGMA.opportunistic.ringInnerBoost;
  }
  if (b.kind === 'account') {
    return ACCOUNT_FRESHNESS_FIGMA[b.freshness] ?? '#B7CEDB';
  }
  if (b.kind === 'asset') {
    const maturity = assetMetaById?.get(b.assetId)?.maturity;
    return assetMaturityFigma(maturity).fill;
  }
  if (b.kind === 'ghost') return '#94A3B8';
  return '#B7CEDB';
}

function bubbleDisplayName(b: Bubble): string {
  if (b.kind === 'ghost') return `NA- ${b.ucName}`;
  return bubbleLabel(b);
}

/**
 * Right-side compact stats line — mirrors the Figma "$10.0M, 100 Opps, X=1.74"
 * format. `axis` provides the current X-axis semantics (word + valueOf) so the
 * X value carries whichever calibration the chart is currently showing.
 */
function bubbleStatsLine(b: Bubble, axis: AxisCfg): string {
  const arr = `$${((b.totalArr ?? 0) / 1e6).toFixed(1)}M`;
  const opps = `${b.oppCount ?? 0} Opps`;
  // Ghost bubbles have no X-axis value (they live in the sidebar column with
  // an independent Y scale) — omit the X= term rather than print a misleading
  // zero.
  if (b.kind === 'ghost') return `${arr}, ${opps}`;
  // Industry rows don't show Influence.
  if (b.kind === 'industry' && axis.isInfluence) return `${arr}, ${opps}`;
  const xVal = axis.valueOf(b);
  const xText = axis.isInfluence ? xVal.toFixed(2) : String(Math.round(xVal));
  return `${arr}, ${opps}, X=${xText}`;
}

interface BubbleHoverListProps {
  state: HoverListState;
  axis: AxisCfg;
  assetMetaById: Map<string, { maturity?: string; isNew: boolean }> | undefined;
  /** `drill=true` when the row click carried Shift/Cmd/Ctrl — same drill
   *  semantics as a modifier click on the canvas bubble. */
  onRowClick: (b: Bubble, drill: boolean) => void;
  onClose: () => void;
}

/**
 * Compact popover listing every bubble under the cursor (or every bubble
 * captured by the last click if locked). Rows are clickable — clicking a row
 * closes the popover and forwards to `onRowClick`, which mirrors the semantics
 * of the old single-hit "click bubble → open detail panel" flow. In multi-chart
 * mode `onRowClick` still opens the "Unable to View Details" modal since it
 * routes through the same `onBubbleClick` prop that used to sit on the canvas.
 *
 * Positioning (E1): sticks to the cursor (px/py) offset by +14/-8 so it doesn't
 * hover directly under the pointer. Clamped inside the canvas so it never
 * disappears off the right edge of the chart card.
 */
function BubbleHoverList({
  state,
  axis,
  assetMetaById,
  onRowClick,
  onClose,
}: BubbleHoverListProps) {
  const { bubbles, px, py, locked } = state;
  // Only surface the drill hint when the popover actually contains a
  // drillable bubble (industry or UC). Asset / Account / Ghost popovers stay
  // uncluttered — the hint would be a lie there.
  const hasDrillable = bubbles.some((b) => b.kind === 'industry' || b.kind === 'uc');
  return (
    <div
      className="ivt-ipo__hoverlist"
      style={{
        position: 'absolute',
        left: px + 14,
        top: py - 8,
        pointerEvents: locked ? 'auto' : 'none',
      }}
      role="dialog"
      aria-label={`${bubbles.length} items here`}
    >
      <div className="ivt-ipo__hoverlist-header">
        <span className="ivt-ipo__hoverlist-count">{bubbles.length} items here</span>
        {hasDrillable && (
          <span
            className="ivt-ipo__hoverlist-hint"
            title="按住 Shift / ⌘ / Ctrl 再点击 = 下钻到下一层视图"
          >
            ⇧ + click to drill down
          </span>
        )}
        <button
          type="button"
          className="ivt-ipo__hoverlist-close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <ul className="ivt-ipo__hoverlist-rows">
        {bubbles.map((b) => (
          <li
            key={bubbleStableId(b)}
            className="ivt-ipo__hoverlist-row"
            onClick={(e) => onRowClick(b, isDrillModifier(e))}
          >
            <span
              className="ivt-ipo__hoverlist-swatch"
              style={{ background: bubbleSwatchColor(b, assetMetaById) }}
              aria-hidden="true"
            />
            <span className="ivt-ipo__hoverlist-name" title={bubbleDisplayName(b)}>
              {bubbleDisplayName(b)}
            </span>
            <span className="ivt-ipo__hoverlist-stats">{bubbleStatsLine(b, axis)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
